import React from 'react';
import { createRoot } from 'react-dom/client';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import {
  AppBar,
  Box,
  Button,
  Container,
  CssBaseline,
  CircularProgress,
  FormControl,
  Grid,
  Icon,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Modal,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
  ThemeProvider,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Backdrop,
  Tabs,
  Tab,
  Chip,
  Alert,
  Checkbox,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import WifiIcon from '@mui/icons-material/Wifi';

import theme from './utils/theme';
import { API_URL, fetchWithAuth } from './utils/api';
import LogsView from './components/LogsView';
import SingleDayView from './components/SingleDayView';
import TeacherScheduleView from './components/TeacherScheduleView';
import GradeScheduleView from './components/GradeScheduleView';

const SCHOOL_HOLIDAYS = [
  { start: new Date('2026-10-17T00:00:00Z'), end: new Date('2026-11-01T23:59:59Z') }, 
  { start: new Date('2026-12-19T00:00:00Z'), end: new Date('2027-01-03T23:59:59Z') },
  { start: new Date('2027-01-31T00:00:00Z'), end: new Date('2027-02-07T23:59:59Z') }, 
  { start: new Date('2027-03-20T00:00:00Z'), end: new Date('2027-04-04T23:59:59Z') },
  { start: new Date('2027-06-23T00:00:00Z'), end: new Date('2027-08-22T23:59:59Z') },
];

const getDateByWeekNumber = (targetWeek) => {
  const schoolYearStartDate = new Date('2026-08-24T00:00:00Z');
  const startOfSchoolYearWeek = new Date(schoolYearStartDate);
  startOfSchoolYearWeek.setUTCDate(schoolYearStartDate.getUTCDate() - schoolYearStartDate.getUTCDay());

  let iterDate = new Date(startOfSchoolYearWeek);
  let count = 0;

  while (count < targetWeek) {
    const weekMonday = new Date(iterDate);
    weekMonday.setUTCDate(iterDate.getUTCDate() + 1);
    const weekFriday = new Date(iterDate);
    weekFriday.setUTCDate(iterDate.getUTCDate() + 5);

    let isHolidayWeek = false;
    for (const period of SCHOOL_HOLIDAYS) {
      if (weekMonday >= period.start && weekFriday <= period.end) {
        isHolidayWeek = true;
        break;
      }
    }

    if (!isHolidayWeek) {
      count++;
      if (count === Number(targetWeek)) {
        return weekMonday;
      }
    }

    iterDate.setUTCDate(iterDate.getUTCDate() + 7);
    if (iterDate > new Date('2027-08-22T00:00:00Z')) break;
  }
  return iterDate;
};

function App({ user, onLogout }) {
  // STATE
  const [currentView, setCurrentView] = React.useState('schedule');
  const [currentDay, setCurrentDay] = React.useState(() => {
    const savedDateString = localStorage.getItem('lastViewedDate');
    if (savedDateString) {
      const savedDate = new Date(savedDateString);
      if (!isNaN(savedDate.getTime())) {
        return savedDate;
      }
    }
    return new Date();
  });
  const [data, setData] = React.useState({ schedules: [], teachers: [], subjects: [], rooms: [], grades: [7, 8, 9, 10, 11, 12] });
  const [isModalOpen, setModalOpen] = React.useState(false);
  const [editingEvent, setEditingEvent] = React.useState(null);
  const [newEvent, setNewEvent] = React.useState({
    grade: '', grades: [], timeSlotId: '', timeSlotIds: [], subjectId: '', teacherId: '', roomId: '',
    color: '#ffffff', customSubject: '', customTeacher: '', customRoom: '', isOnline: false, isExam: false
  });
  const [formErrors, setFormErrors] = React.useState({});
  const [submitError, setSubmitError] = React.useState('');
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const [confirmMessage, setConfirmMessage] = React.useState('');
  const [pendingAction, setPendingAction] = React.useState(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [selectedEventIds, setSelectedEventIds] = React.useState([]);
  const [clipboardEvents, setClipboardEvents] = React.useState([]);
  const [cellMenuAnchor, setCellMenuAnchor] = React.useState(null);
  const [activeCellContext, setActiveCellContext] = React.useState(null);
  const [isExportModalOpen, setExportModalOpen] = React.useState(false);
  const [exportSelection, setExportSelection] = React.useState('general');
  const [exportDays, setExportDays] = React.useState({ Monday: true, Tuesday: true, Wednesday: true, Thursday: true, Friday: true });
  const [isExporting, setIsExporting] = React.useState(false);
  const [isLoadingData, setIsLoadingData] = React.useState(true);
  const [isCopyModalOpen, setIsCopyModalOpen] = React.useState(false);
  const [copyTargetDate, setCopyTargetDate] = React.useState(null);
  const [isCopyWeekModalOpen, setIsCopyWeekModalOpen] = React.useState(false);
  const [copyWeekTargetDate, setCopyWeekTargetDate] = React.useState(null);
  const [isSubjectModalOpen, setIsSubjectModalOpen] = React.useState(false);
  const [editingSubjects, setEditingSubjects] = React.useState([]);
  const [newSubjectName, setNewSubjectName] = React.useState('');
  const [newSubjectColor, setNewSubjectColor] = React.useState('#1976d2');
  const [newSubjectTeacher, setNewSubjectTeacher] = React.useState('');
  const [isUsageModalOpen, setIsUsageModalOpen] = React.useState(false);
  const [usageData, setUsageData] = React.useState({ subjectName: '', dates: [] });
  const [isTeacherModalOpen, setIsTeacherModalOpen] = React.useState(false);
  const [editingTeachers, setEditingTeachers] = React.useState([]);
  const [newTeacherName, setNewTeacherName] = React.useState('');
  const [newTeacherEmail, setNewTeacherEmail] = React.useState('');
  const [newTeacherRoom, setNewTeacherRoom] = React.useState('');
  const [teacherToEdit, setTeacherToEdit] = React.useState(null);
  const [subjectToEdit, setSubjectToEdit] = React.useState(null);
  const [roomToEdit, setRoomToEdit] = React.useState(null);
  const [isTeacherUsageModalOpen, setIsTeacherUsageModalOpen] = React.useState(false);
  const [teacherUsageData, setTeacherUsageData] = React.useState({ teacherName: '', dates: [] });
  const [isRoomModalOpen, setIsRoomModalOpen] = React.useState(false);
  const [editingRooms, setEditingRooms] = React.useState([]);
  const [newRoomName, setNewRoomName] = React.useState('');
  const [isRoomUsageModalOpen, setIsRoomUsageModalOpen] = React.useState(false);
  const [roomUsageData, setRoomUsageData] = React.useState({ roomName: '', dates: [] });
  const [isLessonCounterModalOpen, setIsLessonCounterModalOpen] = React.useState(false);
  const [counterStartDate, setCounterStartDate] = React.useState('');
  const [counterEndDate, setCounterEndDate] = React.useState('');
  const [counterSelectedTeacher, setCounterSelectedTeacher] = React.useState('');
  const [counterSelectedSubject, setCounterSelectedSubject] = React.useState('');
  const [lessonCount, setLessonCount] = React.useState(0);
  const [lessonBreakdown, setLessonBreakdown] = React.useState([]);
  const [simultaneousLessons, setSimultaneousLessons] = React.useState([]);
  const [isSendMailModalOpen, setIsSendMailModalOpen] = React.useState(false);
  const [mailTab, setMailTab] = React.useState(0);
  const [emailBody, setEmailBody] = React.useState('');
  const [emailRecipients, setEmailRecipients] = React.useState([]);
  const [selectedRecipients, setSelectedRecipients] = React.useState({});
  const [sendingMail, setSendingMail] = React.useState(false);
  const [selectedTeacher, setSelectedTeacher] = React.useState('');
  const [selectedGrade, setSelectedGrade] = React.useState('');
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isMenuOpen = Boolean(anchorEl);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isRolesModalOpen, setIsRolesModalOpen] = React.useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = React.useState(false);
  const [usersList, setUsersList] = React.useState([]);
  const [newUserEmail, setNewUserEmail] = React.useState('');
  const [newUserName, setNewUserName] = React.useState('');
  const [newUserRole, setNewUserRole] = React.useState('viewer');
  const [isSettingsModalOpen, setIsSettingsModalOpen] = React.useState(false);
  const [allTeachersEmail, setAllTeachersEmail] = React.useState('teachers@krumbach.school');
  const [gradeEmails, setGradeEmails] = React.useState({});
  const [isPickingDate, setIsPickingDate] = React.useState(false);
  const [versionInfo, setVersionInfo] = React.useState({ version: '1.0.0', build: 0 });
  const [exportWeek, setExportWeek] = React.useState('');
  const dateInputRef = React.useRef(null);
  const draggedItem = React.useRef(null);
  const [draggedEvent, setDraggedEvent] = React.useState(null);
  const [dragOverCell, setDragOverCell] = React.useState(null); // { grade, timeSlotId }
  
  const { schedules, teachers, subjects, rooms, grades } = data;

  const SUPER_ADMIN_EMAIL = 'a.bukhariev@krumbach.school';
  const isAdmin = user.role === 'admin' || user.email === SUPER_ADMIN_EMAIL;
  const isEditor = ['admin', 'editor'].includes(user.role) || user.email === SUPER_ADMIN_EMAIL;
  const isCounter = ['admin', 'editor', 'counter'].includes(user.role) || user.email === SUPER_ADMIN_EMAIL;

  const fetchData = React.useCallback(() => {
    setIsLoadingData(true);
    Promise.all([
        fetchWithAuth(`${API_URL}/config`),
        fetchWithAuth(`${API_URL}/data`),
        fetch(`${API_URL}/info`).then(res => res.json())
    ])
    .then(async ([configRes, dataRes, infoData]) => {
        const configData = await configRes.json();
        const mainData = await dataRes.json();
        
        if (configData.allTeachersEmail) setAllTeachersEmail(configData.allTeachersEmail);
        if (configData.gradeEmails) setGradeEmails(configData.gradeEmails);
        if (infoData) setVersionInfo(infoData);

        mainData.subjects.sort((a, b) => a.order - b.order);
        mainData.teachers.sort((a, b) => a.order - b.order);
        mainData.rooms.sort((a, b) => a.order - b.order);
        setData(mainData);
    })
    .catch(err => {
      console.error("Failed to fetch data", err);
    })
    .finally(() => {
        setIsLoadingData(false);
    });
  }, []);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const timeSlots = [
    { id: 1, time: '8:00 - 8:50' }, { id: 2, time: '9:00 - 9:50' }, { id: 3, time: '10:00 - 10:50' },
    { id: 'snack', time: 'Snack' }, { id: 4, time: '11:00 - 11:50' }, { id: 5, time: '12:00 - 12:50' },
    { id: 'lunch', time: '13:00 - 13:50' }, { id: 6, time: '14:00 - 14:50' }, { id: 7, time: '15:00 - 15:50' },
    { id: 8, time: '16:00-16:50' }, { id: 9, time: '17:00-17:50' }, { id: 10, time: '17:50-19:00' },
  ];
  const darkGrayCellStyle = { border: '2px solid #000000', padding: '8px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: '#e0e0e0', height: '50px' };

  const handleMenuOpen = (event) => { setAnchorEl(event.currentTarget); };
  const handleMenuClose = () => { setAnchorEl(null); };

    const handleCleanDay = async () => {
        if (!isEditor) return;
        const year = currentDay.getFullYear();
        const month = String(currentDay.getMonth() + 1).padStart(2, '0');
        const date = String(currentDay.getDate()).padStart(2, '0');
        const dayString = `${year}-${month}-${date}`;
        if (window.confirm(`Are you sure you want to delete all events for ${dayString}? This cannot be undone.`)) {
          try {
            const response = await fetchWithAuth(`${API_URL}/schedules/by-day`, {
              method: 'DELETE',
              body: JSON.stringify({ day: dayString })
            });
            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.message || 'Failed to clear the day.');
            }
            fetchData();
            alert(`Successfully cleared all events for ${dayString}.`);
          } catch (err) {
            console.error("Failed to clean day:", err);
            alert(`An error occurred while cleaning the day: ${err.message}`);
          }
        }
    };
    
  const handleCopyEvents = () => {
      const eventsToCopy = schedules.filter(e => selectedEventIds.includes(e._id));
      setClipboardEvents(eventsToCopy);
      setSelectedEventIds([]); // Clear selection after copying
  };

  const handleCopySingle = (e, event) => {
      e.stopPropagation();
      if (selectedEventIds.length > 0) {
          const eventsToCopy = schedules.filter(evt => selectedEventIds.includes(evt._id));
          if (!selectedEventIds.includes(event._id)) {
              eventsToCopy.push(event);
          }
          setClipboardEvents(eventsToCopy);
      } else {
          setClipboardEvents([event]);
      }
      setSelectedEventIds([]); // Clear any multi-selection
  };

  const handleCellClick = (e, grade, timeSlotId) => {
      if (!isEditor) return;
      setCellMenuAnchor(e.currentTarget);
      setActiveCellContext({ grade, timeSlotId });
  };
  const handlePasteToCell = async (targetGrade, targetTimeSlotId, isForce = false) => {
      if (clipboardEvents.length === 0) return;
      console.log(`Pasting ${clipboardEvents.length} events to G${targetGrade}-T${targetTimeSlotId}, Force: ${isForce}`);

      const year = currentDay.getFullYear();
      const month = String(currentDay.getMonth() + 1).padStart(2, '0');
      const date = String(currentDay.getDate()).padStart(2, '0');
      const dayString = `${year}-${month}-${date}`;

      setIsSaving(true);
      try {
          const eventsToCreate = clipboardEvents.map(evt => {
              const { _id, ...rest } = evt;
              return {
                  ...rest,
                  day: dayString,
                  grade: targetGrade,
                  timeSlotId: targetTimeSlotId,
                  force: isForce
              };
          });

          if (!isForce) {
              for (const evt of eventsToCreate) {
                  if (evt.roomId) {
                      const conflict = schedules.find(s => s.day === evt.day && s.timeSlotId === evt.timeSlotId && s.roomId === evt.roomId);
                      if (conflict) {
                          const msg = `Conflict: Room ${rooms.find(r=>r._id===evt.roomId)?.name} is booked.`;
                          setConfirmMessage(msg);
                          setPendingAction(() => () => handlePasteToCell(targetGrade, targetTimeSlotId, true));
                          setIsConfirmOpen(true);
                          return; 
                      }
                  }
                  if (evt.teacherId) {
                      const conflict = schedules.find(s => s.day === evt.day && s.timeSlotId === evt.timeSlotId && s.teacherId === evt.teacherId);
                      if (conflict) {
                          const msg = `Conflict: Teacher ${teachers.find(t=>t._id===evt.teacherId)?.name} is busy.`;
                          setConfirmMessage(msg);
                          setPendingAction(() => () => handlePasteToCell(targetGrade, targetTimeSlotId, true));
                          setIsConfirmOpen(true);
                          return;
                      }
                  }
              }
          }

          const response = await fetchWithAuth(`${API_URL}/events/batch`, {
              method: 'POST',
              body: JSON.stringify(eventsToCreate)
          });

          if (!response.ok) {
              const errData = await response.json();
              throw new Error(errData.message || 'Failed to paste events');
          }
          
          fetchData();
          setClipboardEvents([]); 
      } catch (err) {
          console.error("Paste error", err);
          const msg = err.message;
          if ((msg.toLowerCase().includes("room") || msg.toLowerCase().includes("teacher")) && !isForce) {
               setConfirmMessage(msg);
               setPendingAction(() => () => handlePasteToCell(targetGrade, targetTimeSlotId, true));
               setIsConfirmOpen(true);
          } else {
               alert("Error pasting events: " + err.message);
          }
      } finally {
          if (!isConfirmOpen) setIsSaving(false);
      }
  };
  const handleOpenSendMailModal = () => {
    fetchWithAuth(`${API_URL}/email_text`)
      .then(res => res.json())
      .then(data => setEmailBody(data.text || ''))
      .catch(err => console.error("Failed to fetch email text", err));
    
    const generalGroup = [{ id: 'all-teachers', name: 'All Teachers', email: allTeachersEmail, type: 'general' }];
    
    const teachersGroup = teachers
        .filter(t => t.email && t.email.trim() !== '')
        .map(t => ({ ...t, id: t._id, type: 'teacher' }));

    const studentsGroup = grades.map(g => ({
        id: `grade-${g}`,
        name: `Grade ${g}`,
        email: gradeEmails[g] || `g${g}@krumbach.school`,
        type: 'student',
        gradeNumber: g
    }));

    setEmailRecipients([...generalGroup, ...teachersGroup, ...studentsGroup]);
    setSelectedRecipients({});
    setMailTab(0);
    setIsSendMailModalOpen(true);
    handleMenuClose();
  };
  
  const handleCloseMailModal = () => {
    setIsSendMailModalOpen(false);
    setSelectedRecipients({});
  };
  const handleConfirmClose = () => {
      setIsConfirmOpen(false);
      setConfirmMessage('');
      setPendingAction(null);
  };

  const handleConfirmAction = () => {
      if (pendingAction) {
          pendingAction();
      }
      handleConfirmClose();
  };
  const handleOpenRolesModal = () => {
      fetchWithAuth(`${API_URL}/users`)
          .then(async res => {
              if (res.ok) {
                  const data = await res.json();
                  if (Array.isArray(data)) {
                      setUsersList(data);
                  } else {
                      console.error('Expected array of users, got:', data);
                      alert('Failed to load users: Unexpected response format');
                  }
              } else {
                  const err = await res.json();
                  alert(`Failed to load users: ${err.message || res.statusText}`);
              }
          })
          .catch(err => {
              console.error(err);
              alert('Network error while loading users');
          });
      setIsRolesModalOpen(true);
  };

  const handleAddUser = () => {
      if (!newUserEmail) return alert('Email is required');
      fetchWithAuth(`${API_URL}/users`, {
          method: 'POST',
          body: JSON.stringify({ email: newUserEmail, name: newUserName, role: newUserRole })
      })
      .then(async res => {
          if (res.ok) {
              const newUser = await res.json();
              setUsersList([...usersList, newUser]);
              setNewUserEmail('');
              setNewUserName('');
              setNewUserRole('viewer');
          } else {
              const err = await res.json();
              alert(err.message || 'Failed to add user');
          }
      });
  };

  const handleDeleteUser = (userId) => {
      if (!window.confirm('Are you sure you want to delete this user?')) return;
      fetchWithAuth(`${API_URL}/users/${userId}`, { method: 'DELETE' })
      .then(res => {
          if (res.ok) {
              setUsersList(usersList.filter(u => u._id !== userId));
          } else {
              alert('Failed to delete user');
          }
      });
  };

  const handleUpdateRole = (userId, newRole) => {
      fetchWithAuth(`${API_URL}/users/${userId}/role`, {
          method: 'PUT',
          body: JSON.stringify({ role: newRole })
      })
      .then(res => {
          if (res.ok) {
              setUsersList(prev => prev.map(u => u._id === userId ? { ...u, role: newRole } : u));
          } else {
              alert('Failed to update role');
          }
      });
  };

  const handleOpenModal = (event = null, cellContext = null) => {
    if (!isEditor) return;
    setFormErrors({});
    setSubmitError('');
    if (event) { 
        setEditingEvent(event); 
        setNewEvent({ ...event, isOnline: !!event.isOnline, isExam: !!event.isExam, grades: [event.grade], timeSlotIds: [event.timeSlotId] }); 
    } 
    else { 
        setEditingEvent(null); 
        const initialGrade = cellContext?.grade || '';
        const initialTimeSlotId = cellContext?.timeSlotId || '';
        
        setNewEvent({ 
            grade: initialGrade, 
            grades: initialGrade ? [initialGrade] : [], 
            timeSlotId: initialTimeSlotId, 
            timeSlotIds: initialTimeSlotId ? [initialTimeSlotId] : [], 
            subjectId: '', teacherId: '', roomId: '', 
            color: '#ffffff', customSubject: '', customTeacher: '', customRoom: '',
            isOnline: false, isExam: false
        }); 
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => { setEditingEvent(null); setModalOpen(false); };

  const handleFormChange = (e) => { 
    const { name, value } = e.target; 
    const updatedEvent = { ...newEvent, [name]: value }; 
    
    if (name === 'subjectId') { 
      const selectedSubject = subjects.find(s => s._id === value); 
      if (selectedSubject) { 
          updatedEvent.color = selectedSubject.color; 
          
          if (selectedSubject.defaultTeacherId) {
              updatedEvent.teacherId = selectedSubject.defaultTeacherId;
              
              const selectedTeacher = teachers.find(t => t._id === selectedSubject.defaultTeacherId);
              if (selectedTeacher && selectedTeacher.defaultRoomId) {
                  updatedEvent.roomId = selectedTeacher.defaultRoomId;
              } else {
                  updatedEvent.roomId = '';
              }
          } else {
              updatedEvent.teacherId = '';
              updatedEvent.roomId = '';
          }
      } 
    }
    
    if (name === 'teacherId') {
        const selectedTeacher = teachers.find(t => t._id === value);
        if (selectedTeacher && selectedTeacher.defaultRoomId) {
            updatedEvent.roomId = selectedTeacher.defaultRoomId;
        } else {
            updatedEvent.roomId = '';
        }
    }

    setNewEvent(updatedEvent); 
  };
  // --- LIVE UPDATE & AUTO-DAY SWITCHER (Viewer Mode Only) ---
  const lastCheckDateRef = React.useRef(new Date().getDate());

  React.useEffect(() => {
      // ONLY for Viewers. Anyone with management/counting roles (isCounter) has manual control.
      if (isCounter) return;

      const intervalId = setInterval(() => {
          // SAFETY CHECK: Although Viewers don't edit, they might have the date picker open
          if (isPickingDate) return;

          // 1. Silent Data Refresh
          fetchData();

          // 2. Smart Auto-switch day if midnight passed
          const now = new Date();
          const systemDayOfMonth = now.getDate();
          
          if (systemDayOfMonth !== lastCheckDateRef.current) {
              const isUserViewingOldToday = currentDay.getDate() === lastCheckDateRef.current && 
                                            currentDay.getMonth() === now.getMonth();

              if (isUserViewingOldToday) {
                  setCurrentDay(now); 
              }
              lastCheckDateRef.current = systemDayOfMonth;
          }
      }, 30000); // 30 seconds

      return () => clearInterval(intervalId);
  }, [fetchData, currentDay, isEditor, isPickingDate]);

  const handleSubmit = () => { if (editingEvent) { handleUpdate(false); } else { handleCreate(false); } };

  const handleCreate = async (isForce = false) => {
    setIsSaving(true);
    setSubmitError('');
    if (!isForce) setFormErrors({});
    const gradesToProcess = newEvent.grades && newEvent.grades.length > 0 ? newEvent.grades : (newEvent.grade ? [newEvent.grade] : []);
    const timeSlotsToProcess = newEvent.timeSlotIds && newEvent.timeSlotIds.length > 0 ? newEvent.timeSlotIds : (newEvent.timeSlotId ? [newEvent.timeSlotId] : []);
    
    if (gradesToProcess.length === 0) {
        alert('Please select at least one grade.');
        setIsSaving(false);
        return;
    }
    if (timeSlotsToProcess.length === 0) {
        alert('Please select at least one time slot.');
        setIsSaving(false);
        return;
    }

    const year = currentDay.getFullYear();
    const month = String(currentDay.getMonth() + 1).padStart(2, '0');
    const date = String(currentDay.getDate()).padStart(2, '0');
    const dayString = `${year}-${month}-${date}`;
    
    let basePayload = { ...newEvent, day: dayString };
    if (basePayload.customSubject) { basePayload.subjectId = null; }
    if (basePayload.customTeacher) { basePayload.teacherId = null; }
    if (basePayload.customRoom) { basePayload.roomId = null; }
    if (basePayload.color === '#ffffff' && basePayload.subjectId) { 
        const selectedSubject = subjects.find(s => s._id === basePayload.subjectId); 
        if (selectedSubject) { basePayload.color = selectedSubject.color; } 
    }
    
    if (isForce) {
        basePayload.force = true;
    }

    try {
        for (const grade of gradesToProcess) {
            for (const timeSlotId of timeSlotsToProcess) {
                const payload = { ...basePayload, grade: grade, timeSlotId: timeSlotId };
                delete payload.grades;
                delete payload.timeSlotIds;
                
                if (!isForce) {
                    if (payload.roomId) {
                        const conflictingEvent = schedules.find(event => event.day === payload.day && event.timeSlotId === payload.timeSlotId && event.roomId === payload.roomId);
                        if (conflictingEvent) {
                            const existingSubjectName = conflictingEvent.customSubject || subjects.find(s => s._id === conflictingEvent.subjectId)?.name;
                            const newSubjectName = payload.customSubject || subjects.find(s => s._id === payload.subjectId)?.name;
                
                            if (existingSubjectName !== newSubjectName) {
                                throw new Error(`Room conflict for Grade ${grade} at ${timeSlots.find(t => t.id === timeSlotId)?.time}: This room is already booked for this time slot with a different subject.`);
                            }
                        }
                    }

                    if (payload.teacherId) {
                        const teacherConflictEvent = schedules.find(event => 
                            event.day === payload.day && 
                            event.timeSlotId === payload.timeSlotId && 
                            event.teacherId === payload.teacherId
                        );
                        if (teacherConflictEvent) {
                            const existingSubjectName = teacherConflictEvent.customSubject || subjects.find(s => s._id === teacherConflictEvent.subjectId)?.name;
                            const newSubjectName = payload.customSubject || subjects.find(s => s._id === payload.subjectId)?.name;
                            if (existingSubjectName !== newSubjectName) {
                                throw new Error(`Teacher conflict for Grade ${grade} at ${timeSlots.find(t => t.id === timeSlotId)?.time}: This teacher is already assigned to a different subject at this time.`);
                            }
                        }
                    }
                }

                const res = await fetchWithAuth(`${API_URL}/events`, { 
                    method: 'POST', 
                    body: JSON.stringify(payload) 
                });

                if (!res.ok) {
                    const err = await res.json();
                    throw new Error(err.message || `An unexpected error occurred during creation for Grade ${grade} at ${timeSlots.find(t => t.id === timeSlotId)?.time}.`);
                }
            }
        }
        
        fetchData(); 
        handleCloseModal();
    } catch (err) {
        console.error("Failed to save event", err);
        const msg = err.message;
        
        const isConflict = msg.toLowerCase().includes("room") || msg.toLowerCase().includes("teacher") || msg.toLowerCase().includes("booked") || msg.toLowerCase().includes("assigned");
        
        if (isConflict && !isForce) {
            setConfirmMessage(msg);
            setPendingAction(() => () => handleCreate(true));
            setIsConfirmOpen(true);
        } else {
            setSubmitError(msg);
            const errors = {};
            if (msg.toLowerCase().includes("room")) errors.roomId = true;
            if (msg.toLowerCase().includes("teacher")) errors.teacherId = true;
            setFormErrors(errors);
        }
    } finally {
        setIsSaving(false);
    }
  };

  const handleUpdate = async (isForce = false) => {
    setIsSaving(true);
    setSubmitError('');
    if (!isForce) setFormErrors({});
    const gradesToProcess = newEvent.grades && newEvent.grades.length > 0 ? newEvent.grades : (newEvent.grade ? [newEvent.grade] : []);
    const timeSlotsToProcess = newEvent.timeSlotIds && newEvent.timeSlotIds.length > 0 ? newEvent.timeSlotIds : (newEvent.timeSlotId ? [newEvent.timeSlotId] : []);
    
    if (gradesToProcess.length === 0) {
        alert('Please select at least one grade.');
        setIsSaving(false);
        return;
    }
    if (timeSlotsToProcess.length === 0) {
        alert('Please select at least one time slot.');
        setIsSaving(false);
        return;
    }

    const { _id, ...basePayload } = newEvent;
    if (basePayload.customSubject) { basePayload.subjectId = null; }
    if (basePayload.customTeacher) { basePayload.teacherId = null; }
    if (basePayload.customRoom) { basePayload.roomId = null; }
    
    if (isForce) {
        basePayload.force = true;
    }

    try {
        const firstGrade = gradesToProcess[0];
        const firstTimeSlotId = timeSlotsToProcess[0];
        const updatePayload = { ...basePayload, grade: firstGrade, timeSlotId: firstTimeSlotId };
        delete updatePayload.grades;
        delete updatePayload.timeSlotIds;

        if (!isForce) {
            if (updatePayload.roomId) {
                const conflictingEvent = schedules.find(event => event.day === updatePayload.day && event.timeSlotId === updatePayload.timeSlotId && event.roomId === updatePayload.roomId && event._id !== editingEvent._id);
                if (conflictingEvent) {
                    const existingSubjectName = conflictingEvent.customSubject || subjects.find(s => s._id === conflictingEvent.subjectId)?.name;
                    const newSubjectName = updatePayload.customSubject || subjects.find(s => s._id === updatePayload.subjectId)?.name;
                    if (existingSubjectName !== newSubjectName) {
                        throw new Error('This room is already booked for this time slot with a different subject.');
                    }
                }
            }
            if (updatePayload.teacherId) {
                const teacherConflictEvent = schedules.find(event => 
                    event.day === updatePayload.day && 
                    event.timeSlotId === updatePayload.timeSlotId && 
                    event.teacherId === updatePayload.teacherId &&
                    event._id !== editingEvent._id
                );
                if (teacherConflictEvent) {
                    const existingSubjectName = teacherConflictEvent.customSubject || subjects.find(s => s._id === teacherConflictEvent.subjectId)?.name;
                    const newSubjectName = updatePayload.customSubject || subjects.find(s => s._id === updatePayload.subjectId)?.name;
                    if (existingSubjectName !== newSubjectName) {
                        throw new Error('This teacher is already assigned to a different subject at this time.');
                    }
                }
            }
        }

        const updateRes = await fetchWithAuth(`${API_URL}/events/${editingEvent._id}`, { 
            method: 'PUT',
            body: JSON.stringify(updatePayload) 
        });

        if (!updateRes.ok) {
            const err = await updateRes.json();
            throw new Error(err.message || 'An unexpected error occurred during update.');
        }

        const allCombinations = [];
        for (const grade of gradesToProcess) {
            for (const timeSlotId of timeSlotsToProcess) {
                if (grade === firstGrade && timeSlotId === firstTimeSlotId) continue;
                allCombinations.push({ grade, timeSlotId });
            }
        }

        for (const combo of allCombinations) {
            const createPayload = { ...basePayload, grade: combo.grade, timeSlotId: combo.timeSlotId };
            delete createPayload.grades;
            delete createPayload.timeSlotIds;
            
            const createRes = await fetchWithAuth(`${API_URL}/events`, { 
                method: 'POST', 
                body: JSON.stringify(createPayload) 
            });
            
            if (!createRes.ok) {
                const err = await createRes.json();
                throw new Error(err.message || `Failed to create additional event for Grade ${combo.grade} at ${timeSlots.find(t => t.id === combo.timeSlotId)?.time}`);
            }
        }

        fetchData(); 
        handleCloseModal();

    } catch (err) {
        console.error("Failed to update event", err);
        const msg = err.message;

        const isConflict = msg.toLowerCase().includes("room") || msg.toLowerCase().includes("teacher") || msg.toLowerCase().includes("booked") || msg.toLowerCase().includes("assigned");
        
        if (isConflict && !isForce) {
            setConfirmMessage(msg);
            setPendingAction(() => () => handleUpdate(true));
            setIsConfirmOpen(true);
        } else {
            setSubmitError(msg);
            const errors = {};
            if (msg.toLowerCase().includes("room")) errors.roomId = true;
            if (msg.toLowerCase().includes("teacher")) errors.teacherId = true;
            setFormErrors(errors);
        }
    } finally {
        setIsSaving(false);
    }
  };

  const handleDelete = () => {
    if (editingEvent) {
      fetchWithAuth(`${API_URL}/events/${editingEvent._id}`, { method: 'DELETE' })
      .then(() => { fetchData(); handleCloseModal(); }).catch(err => console.error("Failed to delete event", err));
    }
  };

  const handlePrevDay = () => { const newDay = new Date(currentDay); newDay.setDate(newDay.getDate() - 1); setCurrentDay(newDay); };
  const handleNextDay = () => { const newDay = new Date(currentDay); newDay.setDate(newDay.getDate() + 1); setCurrentDay(newDay); };
  const getAllWeeks = () => { return Array.from({ length: 40 }, (_, i) => i + 1); };
  const handleWeekChange = (e) => { const weekNumber = e.target.value; setCurrentDay(getDateByWeekNumber(weekNumber)); };
  
    const formatDate = (date) => {
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    };

  const getEventsForCell = (grade, timeSlotId) => {
      const year = currentDay.getFullYear();
      const month = String(currentDay.getMonth() + 1).padStart(2, '0');
      const date = String(currentDay.getDate()).padStart(2, '0');
      const dayString = `${year}-${month}-${date}`;
      let events = schedules.filter(event => event.day === dayString && event.grade === grade && event.timeSlotId === timeSlotId);
      if (selectedTeacher) {
        events = events.filter(event => event.teacherId === selectedTeacher);
      }
      if (selectedGrade) {
        events = events.filter(event => event.grade === selectedGrade);
      }
      return events.sort((a, b) => {
          const orderA = a.order || 0;
          const orderB = b.order || 0;
          if (orderA !== orderB) return orderA - orderB;
          return String(a._id).localeCompare(String(b._id));
      });
  };

  const dayFormatter = new Intl.DateTimeFormat('en-US', { weekday: 'long' });

  // DRAG & DROP HANDLERS
  const handleDragStart = (e, index) => {
      draggedItem.current = index;
      e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
      e.preventDefault();
  };

  const handleDrop = (e, index, listType) => {
      e.preventDefault();
      const draggedIndex = draggedItem.current;
      if (draggedIndex === null || draggedIndex === index) {
          return;
      }

      let items;
      let setItems;

      if (listType === 'subjects') {
          items = [...editingSubjects];
          setItems = setEditingSubjects;
      } else if (listType === 'teachers') {
          items = [...editingTeachers];
          setItems = setEditingTeachers;
      } else if (listType === 'rooms') {
          items = [...editingRooms];
          setItems = setEditingRooms;
      } else {
          return;
      }

      const [reorderedItem] = items.splice(draggedIndex, 1);
      items.splice(index, 0, reorderedItem);
      setItems(items);
      draggedItem.current = null;
  };

  const handleEventDragStart = (e, event) => {
      if (!isEditor) return;
      setDragOverCell(null); // Clear any stale state
      console.log("Drag Start:", event);
      setDraggedEvent(event);
      e.dataTransfer.setData('eventId', event._id);
      e.dataTransfer.effectAllowed = 'move';
  };

  const handleEventDrop = async (e, targetGrade, targetTimeSlotId, isForce = false, targetEventId = null) => {
      e.preventDefault();
      e.stopPropagation();
      console.log("Drop on:", { targetGrade, targetTimeSlotId, targetEventId });
      
      if (!draggedEvent || !isEditor) {
          console.warn("Drop cancelled: no draggedEvent or not editor");
          return;
      }

      const eventId = draggedEvent._id;
      const originalEvent = schedules.find(ev => ev._id === eventId);
      if (!originalEvent) return;

      // Вычисляем новый порядок (order)
      const cellEvents = getEventsForCell(targetGrade, targetTimeSlotId).filter(ev => ev._id !== eventId);
      let newOrder = 0;

      if (targetEventId) {
          const targetEvent = cellEvents.find(ev => ev._id === targetEventId);
          if (targetEvent) {
              // Ставим ПЕРЕД целевым элементом
              newOrder = (targetEvent.order || 0) - 0.5;
          } else {
              newOrder = cellEvents.length;
          }
      } else {
          // В конец списка
          newOrder = cellEvents.length > 0 ? Math.max(...cellEvents.map(ev => ev.order || 0)) + 1 : 0;
      }

      setIsSaving(true);
      try {
          const { _id, ...basePayload } = originalEvent;
          const payload = {
              ...basePayload,
              grade: targetGrade,
              timeSlotId: targetTimeSlotId,
              order: newOrder,
              force: isForce
          };

          const response = await fetchWithAuth(`${API_URL}/events/${eventId}`, {
              method: 'PUT',
              body: JSON.stringify(payload)
          });

          if (!response.ok) {
              const errData = await response.json();
              throw new Error(errData.message || 'Failed to move event');
          }
          
          fetchData();
          setDraggedEvent(null);
          setDragOverCell(null);
      } catch (err) {
          console.error("Move error", err);
          const msg = err.message;
          if ((msg.toLowerCase().includes("room") || msg.toLowerCase().includes("teacher")) && !isForce) {
               setConfirmMessage(msg + " Do you want to force this move?");
               setPendingAction(() => () => handleEventDrop(e, targetGrade, targetTimeSlotId, true, targetEventId));
               setIsConfirmOpen(true);
          } else {
               alert("Error moving event: " + err.message);
               setDraggedEvent(null);
               setDragOverCell(null);
          }
      } finally {
          if (!isConfirmOpen) setIsSaving(false);
      }
  };

  // EXPORT HANDLERS
  // EXPORT HANDLERS
  const generatePDF = async (isForEmail = false, teacherName = null, grade = null, container, customTableId = null) => {
    const pdf = new jsPDF('landscape', 'pt', 'a4');
    
    // Calculate available PDF dimensions early
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 40;
    const availablePdfWidth = pdfWidth - 2 * margin;
    const availablePdfHeight = pdfHeight - 2 * margin;
    
    let tableId = customTableId; // Use customTableId if provided
    if (!tableId) { // Fallback to existing logic if not provided
        if (teacherName) {
            tableId = '#teacher-schedule-table';
        } else if (grade) {
            tableId = '#grade-schedule-table';
        } else {
            tableId = '#schedule-table';
        }
    }

    const table = container.querySelector(tableId);
    if (!table) {
      const errorMsg = `Could not find table: ${tableId}`;
      console.error(errorMsg);
      if (!isForEmail) alert(errorMsg);
      return Promise.reject(errorMsg);
    }

    // Force precise borders
    const actualTable = table;
    if (actualTable) {
        actualTable.style.border = '0.5px solid #000';
        actualTable.style.borderCollapse = 'collapse';
        actualTable.style.width = '100%';
        const cells = actualTable.querySelectorAll('th, td');
        cells.forEach(cell => { 
            cell.style.border = '0.5px solid #000'; 
            cell.style.padding = '4px 8px'; 
        });
    }

    const canvas = await html2canvas(table, { 
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.85);
    
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const widthRatio = availablePdfWidth / canvasWidth;
    const heightRatio = (availablePdfHeight - 20) / canvasHeight; 
    const scaleFactor = Math.min(widthRatio, heightRatio);

    const imgWidth = canvasWidth * scaleFactor;
    const imgHeight = canvasHeight * scaleFactor;

    const monday = new Date(currentDay);
    while (monday.getDay() !== 1) {
      monday.setDate(monday.getDate() - 1);
    }
    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    const dateRange = `(${formatDate(monday)} - ${formatDate(friday)})`;
        
    let dateText = `Schedule for Week ${getWeekNumber(currentDay)} ${dateRange}`;
    if (teacherName) {
      dateText = `Schedule for ${teacherName} - Week ${getWeekNumber(currentDay)} ${dateRange}`;
    } else if (grade) {
      dateText = `Schedule for Grade ${grade} - Week ${getWeekNumber(currentDay)} ${dateRange}`;
    }
            
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(14);
    pdf.setTextColor(0, 0, 0);
    pdf.text(dateText, margin, margin);
    
    pdf.addImage(imgData, 'JPEG', margin, margin + 20, imgWidth, imgHeight, undefined, 'FAST');

    if (isForEmail) {
      return pdf.output('datauristring');
    } else {
      let fileName = `schedule-W_${getWeekNumber(currentDay)}.pdf`;
      if (teacherName) {
        fileName = `schedule-T_${teacherName.replace(/\s/g, '_')}-W_${getWeekNumber(currentDay)}.pdf`;
      } else if (grade) {
        fileName = `schedule-G_${grade}-W_${getWeekNumber(currentDay)}.pdf`;
      }
      pdf.save(fileName);
    }
  };
                    
  const generateWeeklyPDF = async (isForEmail = false) => {
    const pdf = new jsPDF('landscape', 'pt', 'a4');
    const pdfContainer = document.getElementById('pdf-container');

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 40;
    const availablePdfWidth = pdfWidth - 2 * margin;
    const availablePdfHeight = pdfHeight - 2 * margin;

    const monday = new Date(currentDay);
    while (monday.getDay() !== 1) {
      monday.setDate(monday.getDate() - 1);
    }
    monday.setHours(0, 0, 0, 0);

    const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    let pageCount = 0;

    for (let i = 0; i < weekDays.length; i++) {
      const dayToRender = new Date(monday);
      dayToRender.setDate(monday.getDate() + i);

      if (!isForEmail && !exportDays[weekDays[i]]) continue;

      const componentToRender = (
        <SingleDayView
          day={dayToRender}
          schedules={schedules}
          subjects={subjects}
          teachers={teachers}
          rooms={rooms}
          grades={grades}
          timeSlots={timeSlots}
          handleOpenModal={() => {}}
          isEditor={isEditor}
          darkGrayCellStyle={darkGrayCellStyle}
        />
      );

      await new Promise(resolve => {
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'fixed';
        tempContainer.style.left = '-9999px';
        tempContainer.style.top = '0';
        tempContainer.style.zIndex = '-9999';
        tempContainer.style.width = '1600px'; 
        tempContainer.style.height = 'auto';
        tempContainer.style.overflow = 'visible';
        tempContainer.style.backgroundColor = '#ffffff';
        document.body.appendChild(tempContainer);

        const root = createRoot(tempContainer);
        root.render(componentToRender);

        setTimeout(async () => {
          const wrapper = tempContainer.querySelector('#single-day-table');
          if (!wrapper) {
            console.error(`Could not find wrapper for ${weekDays[i]}`);
            root.unmount();
            document.body.removeChild(tempContainer);
            resolve();
            return;
          }

          // Force precise borders
          const actualTable = wrapper.querySelector('table');
          if (actualTable) {
              actualTable.style.border = '0.5px solid #000';
              actualTable.style.borderCollapse = 'collapse';
              actualTable.style.width = '100%';
              const cells = actualTable.querySelectorAll('th, td');
              cells.forEach(cell => { 
                  cell.style.border = '0.5px solid #000'; 
                  cell.style.padding = '4px 8px'; 
              });
          }

          const canvas = await html2canvas(wrapper, { 
            scale: 2,
            useCORS: true,
            logging: false,
            width: 1600,
            windowWidth: 1600,
            backgroundColor: '#ffffff'
          });

          const imgData = canvas.toDataURL('image/jpeg', 0.85);
          
          if (pageCount > 0) pdf.addPage();
          pageCount++;

          const canvasWidth = canvas.width;
          const canvasHeight = canvas.height;
          const widthRatio = availablePdfWidth / canvasWidth;
          const heightRatio = (availablePdfHeight - 40) / canvasHeight; 
          const scaleFactor = Math.min(widthRatio, heightRatio);

          const imgWidth = canvasWidth * scaleFactor;
          const imgHeight = canvasHeight * scaleFactor;

          const dateText = `${weekDays[i]} - ${formatDate(dayToRender)}`;
          
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(14);
          pdf.setTextColor(0, 0, 0);
          pdf.text(dateText, margin, margin);
          
          pdf.addImage(imgData, 'JPEG', margin, margin + 20, imgWidth, imgHeight, undefined, 'FAST');
          
          root.unmount();
          document.body.removeChild(tempContainer);
          resolve();
        }, 1000); 
      });
    }

    if (isForEmail) {
      return pdf.output('datauristring');
    } else {
      const fileName = `SKIS-Weekly-Schedule-W_${getWeekNumber(currentDay)}.pdf`;
      pdf.save(fileName);
    }
  };
  const handleOpenExportModal = () => { 
    const currentWeek = getWeekNumber(currentDay); 
    setExportWeek(currentWeek === 'Holidays' ? 1 : currentWeek); 
    setExportSelection('general');
    setExportDays({ Monday: false, Tuesday: false, Wednesday: false, Thursday: false, Friday: false });
    setExportModalOpen(true); 
  };
  const handleCloseExportModal = () => { setExportModalOpen(false); setExportWeek(''); setExportDays({ Monday: false, Tuesday: false, Wednesday: false, Thursday: false, Friday: false }); };
  const handleExportDayChange = (e) => { setExportDays({ ...exportDays, [e.target.name]: e.target.checked }); };
  const handleToggleAllExportDays = () => {
    const areAllSelected = Object.values(exportDays).every(day => day === true);
    if (areAllSelected) {
      setExportDays({ Monday: false, Tuesday: false, Wednesday: false, Thursday: false, Friday: false });
    } else {
      setExportDays({ Monday: true, Tuesday: true, Wednesday: true, Thursday: true, Friday: true });
    }
  };
  const handleExport = async () => {
    setIsExporting(true);
    
    // Fix: Always use generateWeeklyPDF for general export, as it handles day selection internally
    if (exportSelection === 'general') {
      await generateWeeklyPDF(false);
    } else {
      let teacherName = null;
      let grade = null;

      if (exportSelection.startsWith('grade-')) {
        grade = parseInt(exportSelection.split('-')[1]);
      } else if (exportSelection !== 'general') {
        const teacher = teachers.find(t => t._id === exportSelection);
        if (teacher) teacherName = teacher.name;
      }
      
                const pdfContainer = document.getElementById('pdf-container');
      
                let componentToRender;
      
                let targetTableId;
      
                if (teacherName) {
      
                  const teacher = teachers.find(t => t.name === teacherName);
      
                  componentToRender = <TeacherScheduleView teacher={teacher} schedules={schedules} subjects={subjects} rooms={rooms} timeSlots={timeSlots} currentDay={currentDay} grades={grades} handleOpenModal={() => {}} isEditor={isEditor} />;
      
                  targetTableId = '#teacher-schedule-table';
      
                } else if (grade) {
      
                  componentToRender = <GradeScheduleView grade={grade} schedules={schedules} subjects={subjects} teachers={teachers} rooms={rooms} timeSlots={timeSlots} currentDay={currentDay} handleOpenModal={() => {}} isEditor={isEditor} />;
      
                  targetTableId = '#grade-schedule-table';
      
                } else {
      
                  componentToRender = <SingleDayView day={currentDay} schedules={schedules} subjects={subjects} teachers={teachers} rooms={rooms} grades={grades} timeSlots={timeSlots} handleOpenModal={() => {}} isEditor={isEditor} darkGrayCellStyle={darkGrayCellStyle} />;
      
                  targetTableId = '#single-day-table';
      
                }
      
      
      
                await new Promise(resolve => {
      
                  const tempContainer = document.createElement('div');
                  tempContainer.style.position = 'fixed';
                  tempContainer.style.left = '-9999px';
                  tempContainer.style.top = '0';
                  tempContainer.style.zIndex = '-9999';
                  tempContainer.style.width = '1900px'; 
                  tempContainer.style.height = 'auto';
                  tempContainer.style.overflow = 'visible';
                  tempContainer.style.backgroundColor = '#ffffff';
      
                  document.body.appendChild(tempContainer);
      
      
      
                  const root = createRoot(tempContainer);
      
                  root.render(componentToRender);
      
      
      
                  setTimeout(async () => {
      
                    await generatePDF(false, teacherName, grade, tempContainer, targetTableId);
      
                    root.unmount();
      
                    document.body.removeChild(tempContainer);
      
                    resolve();
      
                  }, 500);
      
                });
    }
    
    setIsExporting(false);
    handleCloseExportModal();
  };

  const handleOpenCopyModal = () => { if (isEditor) setIsCopyModalOpen(true); };
  const handleCloseCopyModal = () => { setIsCopyModalOpen(false); setCopyTargetDate(null); };
  const handlePasteDay = async () => {
    if (!copyTargetDate) { alert('Please select a target date.'); return; }
    
    const sourceYear = currentDay.getFullYear();
    const sourceMonth = String(currentDay.getMonth() + 1).padStart(2, '0');
    const sourceDate = String(currentDay.getDate()).padStart(2, '0');
    const dayString = `${sourceYear}-${sourceMonth}-${sourceDate}`;

    const targetYear = copyTargetDate.getFullYear();
    const targetMonth = String(copyTargetDate.getMonth() + 1).padStart(2, '0');
    const targetDate = String(copyTargetDate.getDate()).padStart(2, '0');
    const targetDayString = `${targetYear}-${targetMonth}-${targetDate}`;
    
    const eventsToCopy = schedules.filter(event => event.day === dayString);
    
    if (eventsToCopy.length === 0) { 
      alert('There is nothing to copy from the selected day.'); 
      handleCloseCopyModal(); 
      return; 
    }

    try {
      const deleteResponse = await fetchWithAuth(`${API_URL}/schedules/by-day`, {
        method: 'DELETE',
        body: JSON.stringify({ day: targetDayString })
      });

      if (!deleteResponse.ok) {
        const errorData = await deleteResponse.json();
        throw new Error(errorData.message || 'Failed to clear target day.');
      }

      const newEvents = eventsToCopy.map(({ _id, ...event }) => ({ ...event, day: targetDayString }));
      const createResponse = await fetchWithAuth(`${API_URL}/events/batch`, { 
        method: 'POST', 
        body: JSON.stringify(newEvents) 
      });

      if (!createResponse.ok) { 
        throw new Error('Failed to paste schedule.'); 
      }
      
      fetchData(); 
      alert(`Successfully replaced schedule on ${formatDate(copyTargetDate)} with schedule from ${formatDate(currentDay)}.`);
      handleCloseCopyModal(); 
      setCurrentDay(copyTargetDate);
    } catch (err) { 
      console.error("Failed to paste schedule", err); 
      alert(`An error occurred while pasting the schedule: ${err.message}`); 
    }
  };

  const handleOpenCopyWeekModal = () => { if (isEditor) setIsCopyWeekModalOpen(true); };
  const handleCloseCopyWeekModal = () => { setIsCopyWeekModalOpen(false); setCopyWeekTargetDate(null); };
  const handlePasteWeek = async () => {
    if (!copyWeekTargetDate) {
        alert('Please select a target date.');
        return;
    }

    const sourceDay = new Date(currentDay);
    const sourceDayOfWeek = sourceDay.getDay();
    const mondayOfSourceWeek = new Date(sourceDay);
    mondayOfSourceWeek.setDate(sourceDay.getDate() - sourceDayOfWeek + (sourceDayOfWeek === 0 ? -6 : 1));

    const targetDayOfWeek = copyWeekTargetDate.getDay();
    const mondayOfTargetWeek = new Date(copyWeekTargetDate);
    mondayOfTargetWeek.setDate(copyWeekTargetDate.getDate() - targetDayOfWeek + (targetDayOfWeek === 0 ? -6 : 1));

    let eventsToCreate = [];
    for (let i = 0; i < 5; i++) { // Loop from Monday to Friday
        const sourceDate = new Date(mondayOfSourceWeek);
        sourceDate.setDate(mondayOfSourceWeek.getDate() + i);
        const sourceYear = sourceDate.getFullYear();
        const sourceMonth = String(sourceDate.getMonth() + 1).padStart(2, '0');
        const sourceDay = String(sourceDate.getDate()).padStart(2, '0');
        const sourceDateString = `${sourceYear}-${sourceMonth}-${sourceDay}`;

        const eventsForSourceDay = schedules.filter(event => event.day === sourceDateString);
        
        if (eventsForSourceDay.length > 0) {
            const targetDate = new Date(mondayOfTargetWeek);
            targetDate.setDate(mondayOfTargetWeek.getDate() + i);
            const targetYear = targetDate.getFullYear();
            const targetMonth = String(targetDate.getMonth() + 1).padStart(2, '0');
            const targetDay = String(targetDate.getDate()).padStart(2, '0');
            const targetDateString = `${targetYear}-${targetMonth}-${targetDay}`;

            const newEventsForTargetDay = eventsForSourceDay.map(({ _id, ...event }) => ({
                ...event,
                day: targetDateString
            }));
            eventsToCreate = [...eventsToCreate, ...newEventsForTargetDay];
        }
    }

    if (eventsToCreate.length === 0) {
        alert('There is nothing to copy from the selected week.');
        handleCloseCopyWeekModal();
        return;
    }

    try {
        const deletePromises = [];
        for (let i = 0; i < 5; i++) {
            const targetDate = new Date(mondayOfTargetWeek);
            targetDate.setDate(mondayOfTargetWeek.getDate() + i);
            const targetYear = targetDate.getFullYear();
            const targetMonth = String(targetDate.getMonth() + 1).padStart(2, '0');
            const targetDay = String(targetDate.getDate()).padStart(2, '0');
            const targetDateString = `${targetYear}-${targetMonth}-${targetDay}`;
            deletePromises.push(
                fetchWithAuth(`${API_URL}/schedules/by-day`, {
                    method: 'DELETE',
                    body: JSON.stringify({ day: targetDateString })
                })
            );
        }
        
        const deleteResponses = await Promise.all(deletePromises);
        for (const res of deleteResponses) {
            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || 'Failed to clear target week.');
            }
        }

        const createResponse = await fetchWithAuth(`${API_URL}/events/batch`, {
            method: 'POST',
            body: JSON.stringify(eventsToCreate)
        });
        if (!createResponse.ok) { throw new Error('Failed to paste week schedule.'); }
        
        fetchData();
        alert(`Successfully replaced schedule in the week of ${formatDate(mondayOfTargetWeek)} with schedule from the week of ${formatDate(mondayOfSourceWeek)}.`);
        handleCloseCopyWeekModal();
        setCurrentDay(mondayOfTargetWeek);
    } catch (err) {
        console.error("Failed to paste week schedule", err);
        alert(`An error occurred while pasting the week schedule: ${err.message}`);
    }
  };

  const handleOpenSubjectModal = () => {
    setSubjectToEdit(null);
    setIsSubjectModalOpen(true);
  };
  const handleCloseSubjectModal = () => {
    setIsSubjectModalOpen(false);
    setSubjectToEdit(null);
    setNewSubjectName('');
    setNewSubjectColor('#1976d2');
  };

  const handleOpenUsageModal = (subjectId) => {
    const subject = subjects.find(s => s._id === subjectId);
    if (!subject) return;

    const usageDates = schedules
      .filter(s => s.subjectId === subjectId)
      .map(s => s.day)
      .filter((day, index, self) => self.indexOf(day) === index)
      .sort((a, b) => new Date(a) - new Date(b))
      .map(dayString => {
        const date = new Date(dayString + 'T12:00:00');
        return {
          raw: dayString,
          formatted: formatDate(date)
        };
      });
    
    setUsageData({ subjectName: subject.name, dates: usageDates });
    setIsUsageModalOpen(true);
  };

  const handleCloseUsageModal = () => {
    setIsUsageModalOpen(false);
    setUsageData({ subjectName: '', dates: [] });
  };

  const handleDeleteUsagesForDay = async (subjectId, day) => {
    if (!subjectId || !day) return;
    
    try {
      const response = await fetchWithAuth(`${API_URL}/schedules/by-subject-and-day`, {
        method: 'DELETE',
        body: JSON.stringify({ subjectId, day })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete schedule entries.');
      }

      fetchData(); 

      handleOpenUsageModal(subjectId);
      
      const updatedEditingSubjects = JSON.parse(JSON.stringify(subjects.map(s => ({...s, isUsed: schedules.some(sc => sc.subjectId === s.id && !(sc.subjectId === subjectId && sc.day === day))}))) );
      setEditingSubjects(updatedEditingSubjects);


    } catch (error) {
      console.error("Error deleting usages for day:", error);
      alert(`An error occurred: ${error.message}`);
    }
  };

  const handleOpenTeacherModal = () => {
    setTeacherToEdit(null);
    setIsTeacherModalOpen(true);
  };

  const handleCloseTeacherModal = () => {
    setIsTeacherModalOpen(false);
    setTeacherToEdit(null);
    setNewTeacherName('');
  };

  const handleOpenTeacherUsageModal = (teacherId) => {
    const teacher = teachers.find(t => t._id === teacherId);
    if (!teacher) return;

    const usageDates = schedules
      .filter(s => s.teacherId === teacherId)
      .map(s => s.day)
      .filter((day, index, self) => self.indexOf(day) === index)
      .sort((a, b) => new Date(a) - new Date(b))
      .map(dayString => ({
        raw: dayString,
        formatted: formatDate(new Date(dayString + 'T12:00:00'))
      }));
    
    setTeacherUsageData({ teacherName: teacher.name, dates: usageDates });
    setIsTeacherUsageModalOpen(true);
  };

  const handleCloseTeacherUsageModal = () => {
    setIsTeacherUsageModalOpen(false);
    setTeacherUsageData({ teacherName: '', dates: [] });
  };

  const handleDeleteTeacherUsagesForDay = async (teacherId, day) => {
    if (!teacherId || !day) return;
    try {
      const response = await fetchWithAuth(`${API_URL}/schedules/by-teacher-and-day`, {
        method: 'DELETE',
        body: JSON.stringify({ teacherId, day })
      });
      if (!response.ok) throw new Error(await response.text());
      fetchData();
      handleOpenTeacherUsageModal(teacherId);
    } catch (error) {
      console.error("Error deleting teacher usages for day:", error);
      alert(`An error occurred: ${error.message}`);
    }
  };

  const handleOpenRoomModal = () => {
    setRoomToEdit(null);
    setIsRoomModalOpen(true);
  };

  const handleCloseRoomModal = () => {
    setIsRoomModalOpen(false);
    setRoomToEdit(null);
    setNewRoomName('');
  };

  const handleOpenRoomUsageModal = (roomId) => {
    const room = rooms.find(r => r._id === roomId);
    if (!room) return;

    const usageDates = schedules
      .filter(s => s.roomId === roomId)
      .map(s => s.day)
      .filter((day, index, self) => self.indexOf(day) === index)
      .sort((a, b) => new Date(a) - new Date(b))
      .map(dayString => ({
        raw: dayString,
        formatted: formatDate(new Date(dayString + 'T12:00:00'))
      }));
    
    setRoomUsageData({ roomName: room.name, dates: usageDates });
    setIsRoomUsageModalOpen(true);
  };

  const handleCloseRoomUsageModal = () => {
    setIsRoomUsageModalOpen(false);
    setRoomUsageData({ roomName: '', dates: [] });
  };

  const handleDeleteRoomUsagesForDay = async (roomId, day) => {
    if (!roomId || !day) return;
    try {
      const response = await fetchWithAuth(`${API_URL}/schedules/by-room-and-day`, {
        method: 'DELETE',
        body: JSON.stringify({ roomId, day })
      });
      if (!response.ok) throw new Error(await response.text());
      fetchData();
      handleOpenRoomUsageModal(roomId);
    } catch (error) {
      console.error("Error deleting room usages for day:", error);
      alert(`An error occurred: ${error.message}`);
    }
  };

  const handleSendMail = async () => {
    const recipients = Object.entries(selectedRecipients)
      .filter(([, isSelected]) => isSelected)
      .map(([id]) => emailRecipients.find(r => r.id === id));

    if (!emailBody.trim()) {
      alert('Email body cannot be empty.');
      return;
    }
    if (recipients.length === 0) {
      alert('Please select at least one recipient.');
      return;
    }

    setSendingMail(true);

    const monday = new Date(currentDay);
    while (monday.getDay() !== 1) {
      monday.setDate(monday.getDate() - 1);
    }
    monday.setHours(0, 0, 0, 0);

    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    friday.setHours(23, 59, 59, 999);

    const dateRangeFormatted = `(${formatDate(monday)}-${formatDate(friday)})`;
    const weekNumber = getWeekNumber(currentDay);

    for (const recipient of recipients) {
        try {
            console.log(`Processing email for: ${recipient.name} (${recipient.email}) [Type: ${recipient.type}]`);
            const pdfContainer = document.getElementById('pdf-container');
            let pdfDataUri = null;
            let fileName = '';
            let subject = `SKIS Week ${weekNumber} Schedule ${dateRangeFormatted}`;

            if (recipient.type === 'general') {
                pdfDataUri = await generateWeeklyPDF(true);
                fileName = `SKIS_Week_${weekNumber}_Schedule_${dateRangeFormatted}.pdf`;
            } 
            else if (recipient.type === 'teacher') {
                const teacher = teachers.find(t => t._id === recipient.id);
                if (!teacher) {
                    console.warn(`Teacher not found: ${recipient.id}`);
                    continue;
                }
                const componentToRender = <TeacherScheduleView teacher={teacher} schedules={schedules} subjects={subjects} rooms={rooms} timeSlots={timeSlots} currentDay={currentDay} grades={grades} handleOpenModal={() => {}} isEditor={isEditor} />;
                
                pdfDataUri = await new Promise(res => {
                    const tempContainer = document.createElement('div');
                    tempContainer.style.position = 'fixed';
                    tempContainer.style.left = '-9999px';
                    tempContainer.style.top = '0';
                    tempContainer.style.zIndex = '-9999';
                    tempContainer.style.width = '1900px'; 
                    tempContainer.style.height = 'auto';
                    tempContainer.style.overflow = 'visible';
                    tempContainer.style.backgroundColor = '#ffffff';
                    document.body.appendChild(tempContainer);

                    const root = createRoot(tempContainer);
                    root.render(componentToRender);

                    setTimeout(async () => {
                        const dataUri = await generatePDF(true, teacher.name, null, tempContainer, '#teacher-schedule-table');
                        root.unmount();
                        document.body.removeChild(tempContainer);
                        res(dataUri);
                    }, 500);
                });
                fileName = `SKIS_Week_${weekNumber}_${recipient.name.replace(/\s/g, '_')}_Schedule_${dateRangeFormatted}.pdf`;
            } 
            else if (recipient.type === 'student') {
                const grade = recipient.gradeNumber;
                const componentToRender = <GradeScheduleView grade={grade} schedules={schedules} subjects={subjects} teachers={teachers} rooms={rooms} timeSlots={timeSlots} currentDay={currentDay} handleOpenModal={() => {}} isEditor={isEditor} />;
                
                pdfDataUri = await new Promise(res => {
                    const tempContainer = document.createElement('div');
                    tempContainer.style.position = 'fixed';
                    tempContainer.style.left = '-9999px';
                    tempContainer.style.top = '0';
                    tempContainer.style.zIndex = '-9999';
                    tempContainer.style.width = '1900px'; 
                    tempContainer.style.height = 'auto';
                    tempContainer.style.overflow = 'visible';
                    tempContainer.style.backgroundColor = '#ffffff';
                    document.body.appendChild(tempContainer);

                    const root = createRoot(tempContainer);
                    root.render(componentToRender);
                    
                    setTimeout(async () => {
                        const dataUri = await generatePDF(true, null, grade, tempContainer, '#grade-schedule-table');
                        root.unmount();
                        document.body.removeChild(tempContainer);
                        res(dataUri);
                    }, 500);
                });
                fileName = `SKIS_Week_${weekNumber}_Grade_${grade}_Schedule_${dateRangeFormatted}.pdf`;
            }

            if (!pdfDataUri) throw new Error('PDF generation failed.');

            const response = await fetchWithAuth(`${API_URL}/send-schedule`, {
                method: 'POST',
                body: JSON.stringify({ emailBody, recipient, subject, fileName, pdfDataUri })
            });
            
            if (!response.ok) throw new Error((await response.json()).message || 'Failed to send email');
            console.log(`Email sent to ${recipient.email} via Gmail API.`);

        } catch (err) {
            console.error(`Error sending to ${recipient.name}:`, err);
            alert(`Error sending to ${recipient.name}: ${err.message}`);
        }
    }
    
    setSendingMail(false);
    alert('Finished sending emails.');
    handleCloseMailModal();
  };


  // EFFECTS
  React.useEffect(() => {
    if (isPickingDate) { dateInputRef.current?.focus(); }
  }, [isPickingDate]);

  React.useEffect(() => {
    if (currentDay && !isNaN(currentDay)) { localStorage.setItem('lastViewedDate', currentDay.toISOString()); }
  }, [currentDay]);
  
  React.useEffect(() => {
    const realToday = new Date();
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1));
    monday.setHours(0, 0, 0, 0);

    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    friday.setHours(23, 59, 59, 999);

    if (!isCounter) {
      if (currentDay < monday || currentDay > friday) {
         setCurrentDay(realToday);
      }
    }
  }, [isCounter, currentDay]);

  React.useEffect(() => {
    if (!counterStartDate || !counterEndDate || !counterSelectedTeacher) {
      setLessonCount(0);
      setLessonBreakdown([]);
      setSimultaneousLessons([]);
      return;
    }

    const start = new Date(counterStartDate);
    const end = new Date(counterEndDate);

    const filteredSchedules = schedules.filter(event => {
      const eventDate = new Date(event.day);
      const isDateInRange = eventDate >= start && eventDate <= end;
      const isTeacherMatch = event.teacherId === counterSelectedTeacher;
      const isSubjectMatch = !counterSelectedSubject || event.subjectId === counterSelectedSubject;
      
      return isDateInRange && isTeacherMatch && isSubjectMatch;
    });

    const breakdownData = {}; // { grade: { subjectId: count } }
    const timeSlotGroups = {}; // { day-timeSlotId: [events] }

    filteredSchedules.forEach(event => {
      if (event.timeSlotId) {
        const { grade, subjectId } = event;
        const g = grade || 'N/A';
        const s = subjectId || 'custom';
        
        if (!breakdownData[g]) breakdownData[g] = {};
        if (!breakdownData[g][s]) breakdownData[g][s] = 0;
        breakdownData[g][s] += 1;

        const timeKey = `${event.day}-${event.timeSlotId}`;
        if (!timeSlotGroups[timeKey]) timeSlotGroups[timeKey] = [];
        timeSlotGroups[timeKey].push(event);
      }
    });

    setLessonCount(Object.keys(timeSlotGroups).length);

    const formattedBreakdown = [];
    Object.keys(breakdownData).forEach(grade => {
        Object.keys(breakdownData[grade]).forEach(subId => {
            formattedBreakdown.push({
                grade,
                subjectId: subId,
                count: breakdownData[grade][subId]
            });
        });
    });
    // Sort by grade, then by subject
    formattedBreakdown.sort((a, b) => {
        if (a.grade !== b.grade) return a.grade.toString().localeCompare(b.grade.toString(), undefined, {numeric: true});
        return 0;
    });
    setLessonBreakdown(formattedBreakdown);

    // Filter groups where teacher is in multiple classes/subjects at once
    const simultaneous = Object.entries(timeSlotGroups)
      .filter(([key, events]) => events.length > 1)
      .map(([key, events]) => ({
        day: key.split('-').slice(0, 3).join('-'),
        timeSlotId: key.split('-')[3],
        events
      }))
      .sort((a, b) => new Date(b.day) - new Date(a.day));
    
    setSimultaneousLessons(simultaneous);

  }, [counterStartDate, counterEndDate, counterSelectedTeacher, counterSelectedSubject, schedules]);
  
  const getWeekNumber = (d) => {
    const schoolYearStartDate = new Date('2026-08-24T00:00:00Z');
    const startOfSchoolYearWeek = new Date(schoolYearStartDate);
    startOfSchoolYearWeek.setUTCDate(schoolYearStartDate.getUTCDate() - schoolYearStartDate.getUTCDay());

    let currentDayUTC = new Date(d.toISOString().split('T')[0] + 'T00:00:00Z');

    for (const period of SCHOOL_HOLIDAYS) { 
        if (currentDayUTC >= period.start && currentDayUTC <= period.end) { return 'Holidays'; } 
    }
    
    if (currentDayUTC < schoolYearStartDate) return 1;

    let weekCount = 0;
    let iterDate = new Date(startOfSchoolYearWeek);
    
    const targetWeekStart = new Date(currentDayUTC);
    targetWeekStart.setUTCDate(currentDayUTC.getUTCDate() - currentDayUTC.getUTCDay());

    while (iterDate <= targetWeekStart) {
        const weekMonday = new Date(iterDate);
        weekMonday.setUTCDate(iterDate.getUTCDate() + 1);
        const weekFriday = new Date(iterDate);
        weekFriday.setUTCDate(iterDate.getUTCDate() + 5);
        
        let isHolidayWeek = false;
        for (const period of SCHOOL_HOLIDAYS) {
            if (weekMonday >= period.start && weekFriday <= period.end) {
                isHolidayWeek = true;
                break;
            }
        }

        if (!isHolidayWeek) {
            weekCount++;
        }
        
        iterDate.setUTCDate(iterDate.getUTCDate() + 7);
    }

    return weekCount;
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar position="static" sx={{ backgroundColor: '#EF333F' }}>
        <Toolbar>
          <Box component="div" sx={{ flexGrow: currentView !== 'schedule' ? 1 : 0, mr: 2, display: 'flex', alignItems: 'center' }}>
            <img src="/v3-2.png" alt="Schloss Krumbach Timetable" style={{ height: '40px', verticalAlign: 'middle', border: '2px solid white', borderRadius: '4px' }} />
             {currentView !== 'schedule' && (
                <Button color="inherit" onClick={() => setCurrentView('schedule')} sx={{ ml: 2, textDecoration: 'underline' }}>
                    Back to Schedule
                </Button>
            )}
          </Box>

          {!isEditor && currentView === 'schedule' && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: 1, color: 'white' }}>
                <IconButton onClick={handlePrevDay} color="inherit" disabled={currentDay <= new Date('2026-08-24T00:00:00Z')}><ArrowBackIosIcon /></IconButton>
                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ p: 1, whiteSpace: 'nowrap' }}>
                  {`${dayFormatter.format(currentDay)} - ${formatDate(currentDay)}`}
                </Typography>
                <IconButton onClick={handleNextDay} color="inherit" disabled={currentDay >= new Date('2027-08-22T23:59:59Z')}><ArrowForwardIosIcon /></IconButton>
            </Box>
          )}

          {isEditor && <Box sx={{ flexGrow: 1 }} />}

          {isCounter && (
            <>
              <Button
                id="menu-button"
                aria-controls={isMenuOpen ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={isMenuOpen ? 'true' : undefined}
                onClick={handleMenuOpen}
                variant="contained"
                color="primary"
                sx={{ mr: 1 }}
              >
                MENU
              </Button>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'menu-button',
                }}
              >
                {isEditor && <MenuItem onClick={() => { handleOpenCopyModal(); handleMenuClose(); }}>Copy Day</MenuItem>}
                {isEditor && <MenuItem onClick={() => { handleCleanDay(); handleMenuClose(); }}>Clean Day</MenuItem>}
                {isEditor && <MenuItem onClick={() => { handleOpenCopyWeekModal(); handleMenuClose(); }}>Copy Week</MenuItem>}
                {isEditor && <MenuItem onClick={() => { handleOpenSubjectModal(); handleMenuClose(); }}>Edit Subjects</MenuItem>}
                {isEditor && <MenuItem onClick={() => { handleOpenTeacherModal(); handleMenuClose(); }}>Edit Teachers</MenuItem>}
                {isEditor && <MenuItem onClick={() => { handleOpenRoomModal(); handleMenuClose(); }}>Edit Rooms</MenuItem>}
                {isAdmin && <MenuItem onClick={() => { handleOpenRolesModal(); handleMenuClose(); }}>Edit Roles</MenuItem>}
                {isAdmin && <MenuItem onClick={() => { setIsSettingsModalOpen(true); handleMenuClose(); }}>Settings</MenuItem>}
                {isAdmin && <MenuItem onClick={() => { setIsLogsModalOpen(true); handleMenuClose(); }}>Logs</MenuItem>}
                <MenuItem onClick={() => { setIsLessonCounterModalOpen(true); handleMenuClose(); }}>Lesson Counter</MenuItem>
                <MenuItem onClick={() => { handleOpenExportModal(); handleMenuClose(); }}>Export to PDF</MenuItem>
                <Box sx={{ p: 2, borderTop: '1px solid #eee', mt: 1, textAlign: 'center' }}>
                  <Typography variant="caption" color="textSecondary">
                    v{versionInfo.version} (build {versionInfo.build})
                  </Typography>
                </Box>
              </Menu>
              {isEditor && <Button variant="contained" color="primary" sx={{ mr: 2 }} onClick={handleOpenSendMailModal}>Send Mail</Button>}
              {isEditor && <Button variant="contained" color="primary" sx={{ mr: 2 }} onClick={() => handleOpenModal()} disabled={isLoadingData}>Add Event</Button>}
            </>
          )}

          <Button variant="outlined" color="inherit" onClick={onLogout}>Logout</Button>
        </Toolbar>
      </AppBar>
      <Container maxWidth="xl" style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }} sx={{ pb: 8 }}>
        {isEditor ? (
          <>
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', justifyContent: 'center', mb: 2, gap: isMobile ? 2 : 0 }}>
                <FormControl sx={{ minWidth: 120, width: isMobile ? '100%' : 'auto' }}>
                  <InputLabel id="week-select-label" sx={{ fontSize: '1.2rem' }}>Week</InputLabel>
                  <Select labelId="week-select-label" value={getWeekNumber(currentDay)} label="Week" onChange={handleWeekChange} sx={{ fontWeight: 'bold' }}>
                    {Array.from({ length: 40 }, (_, i) => i + 1).map(week => (<MenuItem key={week} value={week} sx={{ textAlign: 'center' }}>{week}</MenuItem>))}
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 150, ml: isMobile ? 0 : 2, width: isMobile ? '100%' : 'auto', backgroundColor: 'white', borderRadius: '4px' }}>
                  <InputLabel id="teacher-filter-label">Filter by Teacher</InputLabel>
                  <Select
                    labelId="teacher-filter-label"
                    id="teacher-filter"
                    value={selectedTeacher}
                    label="Filter by Teacher"
                    onChange={(e) => {
                      setSelectedTeacher(e.target.value);
                      setSelectedGrade('');
                    }}
                  >
                    <MenuItem value="">
                      <em>All Teachers</em>
                    </MenuItem>
                    {teachers.map(teacher => (
                      teacher && <MenuItem key={teacher._id} value={teacher._id}>{teacher.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 150, ml: isMobile ? 0 : 2, width: isMobile ? '100%' : 'auto', backgroundColor: 'white', borderRadius: '4px' }}>
                  <InputLabel id="grade-filter-label">Filter by Grade</InputLabel>
                  <Select
                    labelId="grade-filter-label"
                    id="grade-filter"
                    value={selectedGrade}
                    label="Filter by Grade"
                    onChange={(e) => {
                      setSelectedGrade(e.target.value);
                      setSelectedTeacher('');
                    }}
                  >
                    <MenuItem value="">
                      <em>All Grades</em>
                    </MenuItem>
                    {grades.map(grade => (
                      <MenuItem key={grade} value={grade}>{`Grade ${grade}`}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <IconButton onClick={handlePrevDay}><ArrowBackIosIcon /></IconButton>
                {isPickingDate ? (
                    <input type="date" ref={dateInputRef} style={{ fontSize: isMobile ? '1.2rem' : '1.5rem', border: '1px solid #ccc', borderRadius: '4px', maxWidth: '160px' }}
                      value={currentDay.toISOString().split('T')[0]}
                      onChange={(e) => {
                        if (e.target.value) { const selectedDate = new Date(e.target.value + 'T12:00:00'); setCurrentDay(selectedDate); }
                        setIsPickingDate(false);
                      }}
                      onBlur={() => setIsPickingDate(false)}
                    />
                ) : (
                    <Typography variant={isMobile ? "h6" : "h5"} onClick={() => setIsPickingDate(true)} sx={{ cursor: 'pointer', p: 1, '&:hover': { textDecoration: 'underline' }, textAlign: 'center' }}>
                      {selectedTeacher || selectedGrade ? `Week of ${formatDate(currentDay)}` : `${dayFormatter.format(currentDay)} - ${formatDate(currentDay)}`}
                    </Typography>
                )}
                <IconButton onClick={handleNextDay}><ArrowForwardIosIcon /></IconButton>
            </Box>
          </>
        ) : null}
        {selectedTeacher ? (
          <TeacherScheduleView 
            teacher={teachers.find(t => t && t._id === selectedTeacher)}
            schedules={schedules}
            subjects={subjects}
            rooms={rooms}
            timeSlots={timeSlots}
            currentDay={currentDay}
            grades={grades}
            handleOpenModal={handleOpenModal}
            isEditor={isEditor}
          />
        ) : selectedGrade ? (
          <GradeScheduleView
            grade={selectedGrade}
            schedules={schedules}
            subjects={subjects}
            teachers={teachers}
            rooms={rooms}
            timeSlots={timeSlots}
            currentDay={currentDay}
            handleOpenModal={handleOpenModal}
            isEditor={isEditor}
          />
        ) : (
          <Box id="schedule-table">
            <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed', minWidth: '1800px' }}>
              <thead>
                <tr>
                  <th style={{...darkGrayCellStyle, width: '120px', whiteSpace: 'nowrap'}}>Time</th>
                  {grades.map(grade => <th key={grade} style={{...darkGrayCellStyle, textAlign: 'center', width: '280px'}}>{`Grade ${grade}`}</th>)}
                </tr>
              </thead>
              <tbody>
                {timeSlots.map(timeSlot => {
                  const isSpecialRow = ['snack', 'lunch', 10].includes(timeSlot.id);
                  const rowStyle = { backgroundColor: isSpecialRow ? '#e0e0e0' : 'inherit' };
                  return (
                      <tr key={timeSlot.id}>
                      <td style={{...darkGrayCellStyle, ...rowStyle, width: '120px', whiteSpace: 'nowrap'}}>{timeSlot.time}</td>
                      {grades.map(grade => {
                          const events = getEventsForCell(grade, timeSlot.id);
                          let cellContent;

                          if (events.length > 1) {
                              cellContent = (
                                  <Box sx={{ display: 'flex', gap: '4px', width: '100%', height: '100%' }}>
                                      {events.map(event => {
                                          const subject = event.customSubject ? { name: event.customSubject } : subjects.find(s => s._id === event.subjectId);
                                          const teacher = event.customTeacher ? { name: event.customTeacher } : teachers.find(t => t._id === event.teacherId);
                                          const room = event.customRoom ? { name: event.customRoom } : rooms.find(r => r._id === event.roomId);
                                          const eventColor = event.color || (subject && subject.color ? subject.color : '#808080');
                                          return (
                                              <Box 
                                                key={event._id} 
                                                draggable={isEditor}
                                                onDragStart={(e) => handleEventDragStart(e, event)}
                                                onDragEnd={() => setDragOverCell(null)}
                                                onDragOver={handleDragOver}
                                                onDrop={(e) => handleEventDrop(e, grade, timeSlot.id, false, event._id)}
                                                sx={{ 
                                                    flex: 1, 
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    backgroundColor: eventColor, 
                                                    color: 'white', 
                                                    padding: '4px 8px', 
                                                    borderRadius: '4px', 
                                                    fontSize: '0.8rem', 
                                                    cursor: isEditor ? 'pointer' : 'default', 
                                                    whiteSpace: 'normal', 
                                                    wordBreak: 'break-word', 
                                                    textAlign: 'left',
                                                    position: 'relative', 
                                                    border: selectedEventIds.includes(event._id) ? '3px solid #1976d2' : 'none',
                                                    boxShadow: selectedEventIds.includes(event._id) ? '0 0 5px rgba(25, 118, 210, 0.8)' : 'none',
                                                    '&:hover .event-actions': { opacity: 1 }
                                                }} 
                                                onClick={() => handleOpenModal(event)}
                                              >
                                                  {isEditor && (
                                                      <Box className="event-actions" sx={{ position: 'absolute', top: 0, right: 0, opacity: selectedEventIds.includes(event._id) ? 1 : 0, transition: 'opacity 0.2s', display: 'flex', bgcolor: 'rgba(255,255,255,0.9)', borderRadius: '0 4px 0 4px' }}>
                                                          <IconButton
                                                              size="small"
                                                              onClick={(e) => handleCopySingle(e, event)}
                                                              sx={{ color: '#1976d2', padding: '2px' }}
                                                              title="Copy"
                                                          >
                                                              <ContentCopyIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                                                          </IconButton>
                                                          <Checkbox 
                                                              checked={selectedEventIds.includes(event._id)}
                                                              onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  const id = event._id;
                                                                  setSelectedEventIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
                                                              }}
                                                              sx={{ color: '#1976d2', '&.Mui-checked': { color: '#1976d2' }, padding: '2px' }}
                                                              size="small"
                                                          />
                                                      </Box>
                                                  )}
                                                  <Typography variant="body1"><b>{subject ? subject.name : 'Event'}</b></Typography>
                                                  <Typography variant="body2">{teacher ? teacher.name : 'N/A'} / {room ? room.name : 'N/A'}</Typography>
                                                  {event.isExam && (
                                                      <Box sx={{ mt: 'auto', alignSelf: 'flex-start', padding: '0 4px', height: 18, borderRadius: '4px', bgcolor: '#e60000', border: '1.5px solid #ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                          <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: '#ffffff', letterSpacing: '0.5px', lineHeight: 1 }}>EXAM</Typography>
                                                      </Box>
                                                  )}
                                                  {event.isOnline && (
                                                      <Box sx={{ position: 'absolute', bottom: 4, right: 4, width: 18, height: 18, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                                                          <WifiIcon sx={{ fontSize: '0.75rem', color: '#ffffff' }} />
                                                      </Box>
                                                  )}
                                              </Box>
                                          );
                                      })}
                                  </Box>
                              );
                          } else if (events.length === 1) {
                              const event = events[0];
                              const subject = event.customSubject ? { name: event.customSubject } : subjects.find(s => s._id === event.subjectId);
                              const teacher = event.customTeacher ? { name: event.customTeacher } : teachers.find(t => t._id === event.teacherId);
                              const room = event.customRoom ? { name: event.customRoom } : rooms.find(r => r._id === event.roomId);
                              const eventColor = event.color || (subject && subject.color ? subject.color : '#808080');
                              cellContent = (
                                  <Box 
                                    key={event._id} 
                                    draggable={isEditor}
                                    onDragStart={(e) => handleEventDragStart(e, event)}
                                    onDragEnd={() => setDragOverCell(null)}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleEventDrop(e, grade, timeSlot.id, false, event._id)}
                                    sx={{ 
                                        display: 'flex',
                                        flexDirection: 'column',
                                        backgroundColor: eventColor, 
                                        color: 'white', 
                                        padding: '4px 8px', 
                                        borderRadius: '4px', 
                                        fontSize: '0.8rem', 
                                        cursor: isEditor ? 'pointer' : 'default', 
                                        whiteSpace: 'normal', 
                                        wordBreak: 'break-word', 
                                        textAlign: 'left',
                                        position: 'relative', 
                                        border: selectedEventIds.includes(event._id) ? '3px solid #1976d2' : 'none',
                                        boxShadow: selectedEventIds.includes(event._id) ? '0 0 5px rgba(25, 118, 210, 0.8)' : 'none',
                                        '&:hover .event-actions': { opacity: 1 }
                                    }} 
                                    onClick={() => handleOpenModal(event)}
                                  >
                                      {isEditor && (
                                          <Box className="event-actions" sx={{ position: 'absolute', top: 0, right: 0, opacity: selectedEventIds.includes(event._id) ? 1 : 0, transition: 'opacity 0.2s', display: 'flex', bgcolor: 'rgba(255,255,255,0.9)', borderRadius: '0 4px 0 4px' }}>
                                              <IconButton
                                                  size="small"
                                                  onClick={(e) => handleCopySingle(e, event)}
                                                  sx={{ color: '#1976d2', padding: '2px' }}
                                                  title="Copy"
                                              >
                                                  <ContentCopyIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                                              </IconButton>
                                              <Checkbox 
                                                  checked={selectedEventIds.includes(event._id)}
                                                  onClick={(e) => {
                                                      e.stopPropagation();
                                                      const id = event._id;
                                                      setSelectedEventIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
                                                  }}
                                                  sx={{ color: '#1976d2', '&.Mui-checked': { color: '#1976d2' }, padding: '2px' }}
                                                  size="small"
                                              />
                                          </Box>
                                      )}
                                      <Typography variant="body1"><b>{subject ? subject.name : 'Event'}</b></Typography>
                                      <Typography variant="body2">{teacher ? teacher.name : 'N/A'} / {room ? room.name : 'N/A'}</Typography>
                                      {event.isExam && (
                                          <Box sx={{ mt: 'auto', alignSelf: 'flex-start', padding: '0 4px', height: 18, borderRadius: '4px', bgcolor: '#e60000', border: '1.5px solid #ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                              <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: '#ffffff', letterSpacing: '0.5px', lineHeight: 1 }}>EXAM</Typography>
                                          </Box>
                                      )}
                                      {event.isOnline && (
                                          <Box sx={{ position: 'absolute', bottom: 4, right: 4, width: 18, height: 18, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                                              <WifiIcon sx={{ fontSize: '0.75rem', color: '#ffffff' }} />
                                          </Box>
                                      )}
                                  </Box>
                              );
                          } else {
                              if (timeSlot.id === 'snack') { cellContent = <Typography variant="body1" sx={{fontWeight: 'bold', color: 'black', textAlign: 'center'}}>Snack</Typography>; }
                              else if (timeSlot.id === 'lunch') { cellContent = <Typography variant="body1" sx={{fontWeight: 'bold', color: 'black', textAlign: 'center'}}>Lunchbreak</Typography>; }
                              else if (timeSlot.id === 10) { cellContent = <Typography variant="body1" sx={{fontWeight: 'bold', color: 'black', textAlign: 'center'}}>Homework Time</Typography>; }
                          }

                                                    return (

                                                      <td 
                                                          key={`${grade}-${timeSlot.id}`} 
                                                          onDragOver={(e) => { e.preventDefault(); handleDragOver(e); }}
                                                          onDragEnter={(e) => { 
                                                              e.preventDefault();
                                                              if (isEditor) setDragOverCell({ grade, timeSlotId: timeSlot.id }); 
                                                          }}
                                                          onDragLeave={() => { 
                                                              if (isEditor && dragOverCell?.grade === grade && dragOverCell?.timeSlotId === timeSlot.id) {
                                                                  setDragOverCell(null); 
                                                              }
                                                          }}
                                                          onDrop={(e) => handleEventDrop(e, grade, timeSlot.id)}
                                                          style={{
                                                              ...darkGrayCellStyle, 
                                                              ...rowStyle,
                                                              border: (dragOverCell?.grade === grade && dragOverCell?.timeSlotId === timeSlot.id) ? '3px dashed #1976d2' : darkGrayCellStyle.border,
                                                              backgroundColor: (dragOverCell?.grade === grade && dragOverCell?.timeSlotId === timeSlot.id) ? '#e3f2fd' : (rowStyle.backgroundColor || darkGrayCellStyle.backgroundColor),
                                                              position: 'relative', 
                                    cursor: isEditor ? 'pointer' : 'default',
                                    transition: 'background-color 0.2s'
                                }}
                                className={isEditor ? "editable-cell" : ""}
                                onClick={(e) => handleCellClick(e, grade, timeSlot.id)}
                                onMouseEnter={(e) => { if(isEditor) e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                                onMouseLeave={(e) => { if(isEditor) e.currentTarget.style.backgroundColor = rowStyle.backgroundColor || '#e0e0e0'; }}
                            >
                                {cellContent}
                            </td>
                          );
                      })}
                      </tr>
                  )
                })}
              </tbody>
            </table>
          </Box>
        )}
        
        <Menu
            anchorEl={cellMenuAnchor}
            open={Boolean(cellMenuAnchor)}
            onClose={() => setCellMenuAnchor(null)}
        >
            <MenuItem 
                onClick={() => {
                    setCellMenuAnchor(null);
                    if (activeCellContext) {
                        handlePasteToCell(activeCellContext.grade, activeCellContext.timeSlotId);
                    }
                }} 
                disabled={clipboardEvents.length === 0}
            >
                <ContentCopyIcon sx={{ mr: 1 }} /> Paste {clipboardEvents.length > 0 ? `${clipboardEvents.length} event(s)` : ''}
            </MenuItem>
            <MenuItem onClick={() => { 
                setCellMenuAnchor(null); 
                if (activeCellContext) handleOpenModal(null, activeCellContext); 
            }}>
                <AddIcon sx={{ mr: 1 }} /> Add New Event
            </MenuItem>
        </Menu>
        {isEditor && <>
              <Modal open={isModalOpen} onClose={handleCloseModal}>
                <Box sx={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)', 
                    width: isMobile ? '95%' : 600, 
                    maxHeight: '90vh', 
                    overflowY: 'auto', 
                    bgcolor: 'background.paper', 
                    borderRadius: 2, 
                    boxShadow: '0 24px 48px rgba(0,0,0,0.15)', 
                    p: 0
                }}>
                    <Box sx={{ bgcolor: '#1976d2', p: 2.5, px: 3 }}>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1.25rem' }}>
                            {editingEvent ? 'Edit Event' : 'New Event'}
                        </Typography>
                    </Box>

                    <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 3.5 }}>
                        {submitError && <Alert severity="error" sx={{ borderRadius: 2 }}>{submitError}</Alert>}
                        
                        {/* 1. GRADES */}
                        <FormControl fullWidth>
                            <InputLabel id="lbl-grades" shrink sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#333' }}>Target Grades</InputLabel>
                            <Select 
                                labelId="lbl-grades"
                                multiple
                                value={newEvent.grades || []} 
                                onChange={handleFormChange}
                                name="grades"
                                label="Target Grades"
                                displayEmpty
                                sx={{ fontSize: '1.1rem', fontWeight: 500 }}
                                renderValue={(selected) => {
                                    if (selected.length === 0) return <em style={{ color: '#aaa' }}>Select grades...</em>;
                                    return (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip key={value} label={value} size="small" variant="outlined" sx={{ fontWeight: 700, fontSize: '0.9rem', border: '1px solid #1976d2', color: '#1976d2' }} />
                                            ))}
                                        </Box>
                                    );
                                }}
                            >
                                {grades.map(g => (
                                    <MenuItem key={g} value={g} sx={{ fontSize: '1.1rem', fontWeight: 500, py: 1.2 }}>
                                        <Checkbox checked={(newEvent.grades || []).includes(g)} /> Grade {g}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* 2. TIME SLOTS */}
                        <FormControl fullWidth>
                            <InputLabel id="lbl-time" shrink sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#333' }}>Time Slots</InputLabel>
                            <Select 
                                labelId="lbl-time"
                                multiple
                                value={newEvent.timeSlotIds || []} 
                                onChange={handleFormChange}
                                name="timeSlotIds"
                                label="Time Slots"
                                displayEmpty
                                sx={{ fontSize: '1.1rem', fontWeight: 500 }}
                                renderValue={(selected) => {
                                    if (selected.length === 0) return <em style={{ color: '#aaa' }}>Select time...</em>;
                                    return (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((id) => (
                                                <Chip key={id} label={timeSlots.find(t => t.id === id)?.time} size="small" variant="outlined" sx={{ fontWeight: 700, fontSize: '0.9rem', border: '1px solid #2e7d32', color: '#2e7d32' }} />
                                            ))}
                                        </Box>
                                    );
                                }}
                            >
                                {timeSlots.map(t => (
                                    <MenuItem key={t.id} value={t.id} sx={{ fontSize: '1.1rem', fontWeight: 500, py: 1.2 }}>
                                        <Checkbox checked={(newEvent.timeSlotIds || []).includes(t.id)} /> {t.time}
                                    </MenuItem>
                                ))}                            </Select>
                        </FormControl>

                        <Box sx={{ height: '1px', bgcolor: '#eee', my: 0.5 }} />

                        {/* 3. SUBJECT */}
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#666', letterSpacing: '0.5px' }}>SUBJECT</Typography>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch 
                                                checked={!!newEvent.isExam} 
                                                onChange={(e) => setNewEvent(prev => ({ ...prev, isExam: e.target.checked }))}
                                                color="error"
                                                size="small"
                                            />
                                        }
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.85rem', fontWeight: 700, color: newEvent.isExam ? '#d32f2f' : '#777', cursor: 'pointer' }}>
                                                EXAM
                                            </Box>
                                        }
                                        sx={{ m: 0 }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch 
                                                checked={!!newEvent.isOnline} 
                                                onChange={(e) => setNewEvent(prev => ({ ...prev, isOnline: e.target.checked }))}
                                                color="primary"
                                                size="small"
                                            />
                                        }
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.85rem', fontWeight: 700, color: newEvent.isOnline ? '#1976d2' : '#777', cursor: 'pointer' }}>
                                                <WifiIcon sx={{ fontSize: '1.1rem', color: newEvent.isOnline ? '#1976d2' : '#aaa' }} /> Online
                                            </Box>
                                        }
                                        sx={{ m: 0 }}
                                    />
                                </Box>
                            </Box>
                            <FormControl fullWidth>
                                <Select 
                                    value={newEvent.subjectId} 
                                    onChange={handleFormChange} 
                                    name="subjectId"
                                    displayEmpty
                                    sx={{ bgcolor: '#fff', fontSize: '1.1rem', fontWeight: 600 }}
                                >
                                    <MenuItem value=""><em style={{ color: '#aaa' }}>Choose a Subject...</em></MenuItem>
                                    {subjects.map(s => <MenuItem key={s._id} value={s._id} sx={{ fontSize: '1.1rem', fontWeight: 500, py: 1.2 }}>{s.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <TextField 
                                placeholder="OR type custom subject name" 
                                name="customSubject" 
                                value={newEvent.customSubject} 
                                onChange={handleFormChange} 
                                fullWidth 
                                variant="standard" 
                                sx={{ mt: 1.5 }} 
                                InputProps={{ style: { fontSize: '1rem', fontWeight: 500 } }}
                            />
                        </Box>

                        {/* 4. TEACHER */}
                        <Box>
                            <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 800, color: '#666', letterSpacing: '0.5px' }}>TEACHER</Typography>
                            <FormControl fullWidth error={!!formErrors.teacherId}>
                                <Select 
                                    value={newEvent.teacherId} 
                                    onChange={handleFormChange} 
                                    name="teacherId"
                                    displayEmpty
                                    sx={{ bgcolor: '#fff', fontSize: '1.1rem', fontWeight: 600 }}
                                >
                                    <MenuItem value=""><em style={{ color: '#aaa' }}>Choose a Teacher...</em></MenuItem>
                                    {teachers.map(t => <MenuItem key={t._id} value={t._id} sx={{ fontSize: '1.1rem', fontWeight: 500, py: 1.2 }}>{t.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <TextField 
                                placeholder="OR type custom teacher name" 
                                name="customTeacher" 
                                value={newEvent.customTeacher} 
                                onChange={handleFormChange} 
                                fullWidth 
                                variant="standard" 
                                sx={{ mt: 1.5 }}
                                InputProps={{ style: { fontSize: '1rem', fontWeight: 500 } }}
                            />
                        </Box>

                        {/* 5. ROOM */}
                        <Box>
                            <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 800, color: '#666', letterSpacing: '0.5px' }}>ROOM</Typography>
                            <FormControl fullWidth error={!!formErrors.roomId}>
                                <Select 
                                    value={newEvent.roomId} 
                                    onChange={handleFormChange} 
                                    name="roomId"
                                    displayEmpty
                                    sx={{ bgcolor: '#fff', fontSize: '1.1rem', fontWeight: 600 }}
                                >
                                    <MenuItem value=""><em style={{ color: '#aaa' }}>Choose a Room...</em></MenuItem>
                                    {rooms.map(r => <MenuItem key={r._id} value={r._id} sx={{ fontSize: '1.1rem', fontWeight: 500, py: 1.2 }}>{r.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <TextField 
                                placeholder="OR type custom room name" 
                                name="customRoom" 
                                value={newEvent.customRoom} 
                                onChange={handleFormChange} 
                                fullWidth 
                                variant="standard" 
                                sx={{ mt: 1.5 }}
                                InputProps={{ style: { fontSize: '1rem', fontWeight: 500 } }}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 1 }}>
                             <Typography variant="body1" sx={{ fontWeight: 700 }}>Color Tag:</Typography>
                             <input type="color" name="color" value={newEvent.color} onChange={handleFormChange} style={{ height: 45, width: 80, border: '2px solid #ddd', borderRadius: 6, cursor: 'pointer' }} />
                        </Box>

                    </Box>

                    <Box sx={{ p: 3, px: 4, borderTop: '1px solid #eee', bgcolor: '#fcfcfc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {editingEvent ? (
                            <Button color="error" variant="outlined" onClick={handleDelete} sx={{ fontWeight: 700 }}>Delete</Button>
                        ) : <div />}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button onClick={handleCloseModal} sx={{ fontWeight: 700, color: '#666' }}>Cancel</Button>
                            <Button variant="contained" onClick={handleSubmit} sx={{ px: 5, py: 1.2, fontWeight: 800, fontSize: '1.05rem', borderRadius: 2 }}>
                                {editingEvent ? 'SAVE CHANGES' : 'CREATE EVENT'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
              </Modal>
      </>}
              <Dialog 
                open={isExportModalOpen} 
                onClose={handleCloseExportModal}
                maxWidth="md"
                fullWidth
              >
                <DialogTitle sx={{ fontWeight: 800, textAlign: 'center', pb: 1, pt: 3, fontSize: '1.5rem' }}>
                    Export Schedule to PDF
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel id="export-type-label">Select Schedule Type</InputLabel>
                            <Select
                                labelId="export-type-label"
                                value={exportSelection}
                                label="Select Schedule Type"
                                onChange={(e) => setExportSelection(e.target.value)}
                                sx={{ borderRadius: 2 }}
                            >
                                <MenuItem value="general">General Weekly Schedule</MenuItem>
                                <Box sx={{ px: 2, py: 1, bgcolor: '#f5f5f5', color: 'text.secondary', fontSize: '0.75rem', fontWeight: 800, letterSpacing: '1px' }}>TEACHERS</Box>
                                {teachers.map(teacher => (
                                    <MenuItem key={teacher._id} value={teacher._id}>{teacher.name}</MenuItem>
                                ))}
                                <Box sx={{ px: 2, py: 1, bgcolor: '#f5f5f5', color: 'text.secondary', fontSize: '0.75rem', fontWeight: 800, letterSpacing: '1px' }}>GRADES</Box>
                                {grades.map(grade => (
                                    <MenuItem key={`grade-${grade}`} value={`grade-${grade}`}>{`Grade ${grade}`}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {isExporting ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                                <CircularProgress size={60} thickness={4} />
                                <Typography sx={{ mt: 3, fontWeight: 600, color: 'text.secondary' }}>Generating PDF... Please wait.</Typography>
                            </Box>
                        ) : (
                            <Paper variant="outlined" sx={{ p: 3, width: '100%', borderRadius: 2, bgcolor: exportSelection === 'general' ? '#fafafa' : '#fff', opacity: exportSelection === 'general' ? 1 : 0.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle1" fontWeight={700} color={exportSelection === 'general' ? 'text.primary' : 'text.disabled'}>
                                        Select Days to Include:
                                    </Typography>
                                    <Button 
                                        size="small" 
                                        variant="text"
                                        onClick={handleToggleAllExportDays}
                                        disabled={exportSelection !== 'general'}
                                        sx={{ fontWeight: 700 }}
                                    >
                                        Toggle All
                                    </Button>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', gap: 2, justifyContent: 'space-between' }}>
                                    {Object.keys(exportDays).map((day) => (
                                        <FormControlLabel
                                            key={day}
                                            control={
                                                <Checkbox 
                                                    checked={exportDays[day]} 
                                                    onChange={handleExportDayChange} 
                                                    name={day} 
                                                    color="primary"
                                                    disabled={exportSelection !== 'general'} 
                                                />
                                            }
                                            label={<Typography fontWeight={500}>{day}</Typography>}
                                        />
                                    ))}
                                </Box>
                            </Paper>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, justifyContent: 'center', gap: 2 }}>
                    <Button onClick={handleCloseExportModal} size="large" sx={{ fontWeight: 700, color: 'text.secondary' }}>Cancel</Button>
                    <Button 
                        variant="contained" 
                        size="large" 
                        onClick={handleExport} 
                        disabled={isExporting}
                        sx={{ px: 6, borderRadius: 8, fontWeight: 700, boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)' }}
                    >
                        Export PDF
                    </Button>
                </DialogActions>
              </Dialog>
              <Modal open={isCopyModalOpen} onClose={handleCloseCopyModal}>
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, bgcolor: 'background.paper', border: '2px solid #000', boxShadow: 24, p: 4 }}>
                    <Typography variant="h6" component="h2" sx={{mb: 2}}>Copy Schedule</Typography>
                    <Typography sx={{mb: 2}}>Copying schedule from: <b>{formatDate(currentDay)}</b></Typography>
                    <TextField
                        label="Paste to date"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => {
                            if (e.target.value) {
                                setCopyTargetDate(new Date(e.target.value + 'T12:00:00'));
                            } else {
                                setCopyTargetDate(null);
                            }
                        }}
                    />
                    <Box sx={{mt: 3, display: 'flex', justifyContent: 'flex-end'}}>
                        <Button onClick={handleCloseCopyModal} sx={{mr: 1}}>Cancel</Button>
                        <Button variant="contained" onClick={handlePasteDay} disabled={!copyTargetDate}>Paste</Button>
                    </Box>
                </Box>
              </Modal>
              <Modal open={isCopyWeekModalOpen} onClose={handleCloseCopyWeekModal}>
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, bgcolor: 'background.paper', border: '2px solid #000', boxShadow: 24, p: 4 }}>
                    <Typography variant="h6" component="h2" sx={{mb: 2}}>Copy Week's Schedule</Typography>
                    <Typography sx={{mb: 2}}>Copying schedule from week of: <b>{formatDate(currentDay)}</b></Typography>
                    <TextField
                        label="Paste to any date in target week"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => {
                            if (e.target.value) {
                                setCopyWeekTargetDate(new Date(e.target.value + 'T12:00:00'));
                            } else {
                                setCopyWeekTargetDate(null);
                            }
                        }}
                    />
                    <Box sx={{mt: 3, display: 'flex', justifyContent: 'flex-end'}}>
                        <Button onClick={handleCloseCopyWeekModal} sx={{mr: 1}}>Cancel</Button>
                        <Button variant="contained" onClick={handlePasteWeek} disabled={!copyWeekTargetDate}>Paste</Button>
                    </Box>
                </Box>
              </Modal>
              <Dialog 
                open={isSubjectModalOpen} 
                onClose={handleCloseSubjectModal}
                fullScreen
              >
                <AppBar sx={{ position: 'sticky', top: 0, bgcolor: 'white', color: 'black', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', zIndex: 1100 }}>
                  <Container maxWidth="lg">
                    <Toolbar disableGutters>
                      <IconButton edge="start" color="inherit" onClick={handleCloseSubjectModal} aria-label="close">
                        <ArrowBackIosIcon />
                      </IconButton>
                      <Typography sx={{ ml: 2, flex: 1, fontWeight: 800, letterSpacing: '-0.5px' }} variant="h5">
                        Subject Management
                      </Typography>
                    </Toolbar>
                  </Container>
                </AppBar>
                
                <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', pb: 10 }}>
                  <Container maxWidth="lg" sx={{ mt: 4 }}>
                    
                    {/* ADD NEW SUBJECT - STICKY-LIKE HEADER */}
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 3, 
                            mb: 2, 
                            borderRadius: 4, 
                            border: '1px solid #e0e0e0', 
                            bgcolor: '#f8f9fa',
                            position: 'sticky',
                            top: 80,
                            zIndex: 1000
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '1px' }}>
                            Quick Add New Subject
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    placeholder="Subject Name"
                                    value={newSubjectName}
                                    onChange={(e) => setNewSubjectName(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2.5}>
                                <FormControl size="small" sx={{ width: '180px' }}>
                                    <InputLabel>Default Teacher</InputLabel>
                                    <Select
                                        value={newSubjectTeacher}
                                        label="Default Teacher"
                                        onChange={(e) => setNewSubjectTeacher(e.target.value)}
                                        sx={{ bgcolor: 'white', borderRadius: 2 }}
                                    >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {teachers.map(t => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={2} sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center' }}>
                                <Typography variant="caption" fontWeight="bold">Color:</Typography>
                                <Box sx={{ 
                                    width: 36, 
                                    height: 36, 
                                    borderRadius: '50%', 
                                    bgcolor: newSubjectColor, 
                                    border: '2px solid #ddd', 
                                    position: 'relative', 
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}>
                                    <input
                                        type="color"
                                        value={newSubjectColor}
                                        onChange={(e) => setNewSubjectColor(e.target.value)}
                                        style={{ 
                                            position: 'absolute', 
                                            top: -5, 
                                            left: -5, 
                                            width: '150%', 
                                            height: '150%', 
                                            opacity: 0, 
                                            cursor: 'pointer' 
                                        }}
                                        title="Choose Color"
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    disableElevation
                                    sx={{ borderRadius: 2, py: 1, fontWeight: 700 }}
                                    onClick={() => {
                                        if (newSubjectName.trim()) {
                                            fetchWithAuth(`${API_URL}/subjects`, {
                                                method: 'POST',
                                                body: JSON.stringify({ 
                                                    name: newSubjectName.trim(), 
                                                    color: newSubjectColor, 
                                                    defaultTeacherId: newSubjectTeacher,
                                                    order: subjects.length
                                                })
                                            })
                                            .then(res => {
                                                if(res.ok) {
                                                    setNewSubjectName('');
                                                    setNewSubjectColor('#1976d2');
                                                    setNewSubjectTeacher('');
                                                    fetchData();
                                                } else {
                                                    alert('Failed to add subject');
                                                }
                                            });
                                        }
                                    }}
                                >
                                    Add
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* SUBJECTS LIST */}
                    <TableContainer component={Box} sx={{ overflowX: 'auto' }}>
                      <Table sx={{ minWidth: 1000, tableLayout: 'fixed' }}>
                        <TableHead>
                          <TableRow sx={{ '& th': { fontWeight: 800, color: 'text.secondary', borderBottom: '2px solid #f0f0f0', py: 2 } }}>
                            <TableCell width={80}>Color</TableCell>
                            <TableCell sx={{ width: '350px' }}>Subject Name</TableCell>
                            <TableCell sx={{ width: '280px' }} align="right">Default Teacher</TableCell>
                            <TableCell align="right" width={120}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[...subjects].sort((a, b) => a.name.localeCompare(b.name)).map((subject) => (
                            <TableRow 
                              key={subject._id}
                              sx={{ 
                                '&:hover': { bgcolor: '#fcfcfc' },
                                '& td': { py: 2.5, borderBottom: '1px solid #f5f5f5' }
                              }}
                            >
                              <TableCell>
                                <Box sx={{ 
                                    width: 32, 
                                    height: 32, 
                                    borderRadius: '50%', 
                                    bgcolor: subject.color, 
                                    border: '1px solid #eee'
                                }} />
                              </TableCell>
                              <TableCell>
                                <Typography fontWeight={600} fontSize="1.1rem">{subject.name}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={500} color="text.secondary">
                                    {teachers.find(t => t._id === subject.defaultTeacherId)?.name || 'None'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                    <IconButton 
                                        size="small" 
                                        color="primary"
                                        onClick={() => setSubjectToEdit(subject)}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    {schedules.some(s => s.subjectId === subject._id) && (
                                        <IconButton size="small" onClick={() => handleOpenUsageModal(subject._id)} title="Find Usages">
                                            <SearchIcon fontSize="small" color="primary" />
                                        </IconButton>
                                    )}
                                    <IconButton 
                                        size="small" 
                                        color="error"
                                        onClick={() => {
                                            if(window.confirm(`Delete '${subject.name}'?`)) {
                                                fetchWithAuth(`${API_URL}/subjects/${subject._id}`, { method: 'DELETE' })
                                                .then(res => {
                                                    if(res.ok) fetchData();
                                                    else res.json().then(data => alert(data.message || 'Failed to delete'));
                                                });
                                            }
                                        }}
                                        disabled={schedules.some(s => s.subjectId === subject._id)}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {/* EDIT SUBJECT DIALOG */}
                    {subjectToEdit && (
                        <Dialog open={true} onClose={() => setSubjectToEdit(null)} maxWidth="sm" fullWidth>
                            <DialogTitle sx={{ fontWeight: 800 }}>Edit Subject</DialogTitle>
                            <DialogContent>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                                    <TextField
                                        label="Subject Name"
                                        value={subjectToEdit.name}
                                        onChange={(e) => setSubjectToEdit({ ...subjectToEdit, name: e.target.value })}
                                        fullWidth
                                        variant="outlined"
                                    />
                                    <FormControl fullWidth>
                                        <InputLabel>Default Teacher</InputLabel>
                                        <Select
                                            value={subjectToEdit.defaultTeacherId || ''}
                                            label="Default Teacher"
                                            onChange={(e) => setSubjectToEdit({ ...subjectToEdit, defaultTeacherId: e.target.value })}
                                        >
                                            <MenuItem value=""><em>None</em></MenuItem>
                                            {teachers.map(t => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography variant="body1" fontWeight={700}>Subject Color:</Typography>
                                        <Box sx={{ 
                                            width: 40, height: 40, borderRadius: '50%', bgcolor: subjectToEdit.color, 
                                            border: '2px solid #ddd', position: 'relative', overflow: 'hidden', cursor: 'pointer' 
                                        }}>
                                            <input
                                                type="color"
                                                value={subjectToEdit.color}
                                                onChange={(e) => setSubjectToEdit({ ...subjectToEdit, color: e.target.value })}
                                                style={{ position: 'absolute', top: -5, left: -5, width: '150%', height: '150%', opacity: 0, cursor: 'pointer' }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            </DialogContent>
                            <DialogActions sx={{ p: 3 }}>
                                <Button onClick={() => setSubjectToEdit(null)}>Cancel</Button>
                                <Button 
                                    variant="contained" 
                                    onClick={() => {
                                        fetchWithAuth(`${API_URL}/subjects/${subjectToEdit._id}`, {
                                            method: 'PUT',
                                            body: JSON.stringify({ 
                                                name: subjectToEdit.name, 
                                                color: subjectToEdit.color, 
                                                defaultTeacherId: subjectToEdit.defaultTeacherId,
                                                order: subjectToEdit.order
                                            })
                                        })
                                        .then(res => {
                                            if(res.ok) {
                                                fetchData();
                                                setSubjectToEdit(null);
                                            } else {
                                                alert('Failed to update subject');
                                            }
                                        });
                                    }}
                                >
                                    Save Changes
                                </Button>
                            </DialogActions>
                        </Dialog>
                    )}

                    {subjects.length === 0 && (
                        <Box sx={{ py: 10, textAlign: 'center' }}>
                            <Typography variant="h6" color="text.disabled">No subjects added yet.</Typography>
                        </Box>
                    )}
                  </Container>
                </Box>
              </Dialog>

              <Modal open={isUsageModalOpen} onClose={handleCloseUsageModal}>
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, bgcolor: 'background.paper', border: '2px solid #000', boxShadow: 24, p: 4 }}>
                    <Typography variant="h6" component="h2" sx={{mb: 2}}>
                        Usage of "{usageData.subjectName}"
                    </Typography>
                    <Typography variant="body1" sx={{mb: 2}}>
                        This subject is used on the following dates. Click delete to remove all entries for a specific day.
                    </Typography>
                    <Box sx={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ccc', p: 1, mb: 2 }}>
                        {usageData.dates.length > 0 ? (
                            usageData.dates.map((dateInfo, index) => (
                                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 0.5 }}>
                                    <Typography>{dateInfo.formatted}</Typography>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => handleDeleteUsagesForDay(subjects.find(s => s.name === usageData.subjectName)?._id, dateInfo.raw)}
                                        title={`Delete all '${usageData.subjectName}' entries for ${dateInfo.formatted}`}
                                    >
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </Box>
                            ))
                        ) : (
                            <Typography>No usages found. You can now delete this subject.</Typography>
                        )}
                    </Box>
                    <Box sx={{mt: 3, display: 'flex', justifyContent: 'flex-end'}}>
                        <Button onClick={handleCloseUsageModal}>Close</Button>
                    </Box>
                </Box>
              </Modal>
    
              <Dialog 
                open={isTeacherModalOpen} 
                onClose={handleCloseTeacherModal}
                fullScreen
              >
                <AppBar sx={{ position: 'sticky', top: 0, bgcolor: 'white', color: 'black', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', zIndex: 1100 }}>
                  <Container maxWidth="lg">
                    <Toolbar disableGutters>
                      <IconButton edge="start" color="inherit" onClick={handleCloseTeacherModal} aria-label="close">
                        <ArrowBackIosIcon />
                      </IconButton>
                      <Typography sx={{ ml: 2, flex: 1, fontWeight: 800, letterSpacing: '-0.5px' }} variant="h5">
                        Teacher Management
                      </Typography>
                    </Toolbar>
                  </Container>
                </AppBar>

                <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', pb: 10 }}>
                  <Container maxWidth="lg" sx={{ mt: 4 }}>
                    
                    {/* ADD NEW TEACHER - STICKY HEADER */}
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 3, 
                            mb: 2, 
                            borderRadius: 4, 
                            border: '1px solid #e0e0e0', 
                            bgcolor: '#f8f9fa',
                            position: 'sticky',
                            top: 80,
                            zIndex: 1000
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '1px' }}>
                            Quick Add New Teacher
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    placeholder="Teacher Name"
                                    value={newTeacherName}
                                    onChange={(e) => setNewTeacherName(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4.5}>
                                <TextField
                                    placeholder="Email Address"
                                    value={newTeacherEmail}
                                    onChange={(e) => setNewTeacherEmail(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2.5}>
                                <FormControl size="small" sx={{ width: '180px' }}>
                                    <InputLabel>Default Room</InputLabel>
                                    <Select
                                        value={newTeacherRoom}
                                        label="Default Room"
                                        onChange={(e) => setNewTeacherRoom(e.target.value)}
                                        sx={{ bgcolor: 'white', borderRadius: 2 }}
                                    >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {rooms.map(r => <MenuItem key={r._id} value={r._id}>{r.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    disableElevation
                                    sx={{ borderRadius: 2, py: 1, fontWeight: 700 }}
                                    onClick={() => {
                                        if (newTeacherName.trim()) {
                                            fetchWithAuth(`${API_URL}/teachers`, {
                                                method: 'POST',
                                                body: JSON.stringify({ 
                                                    name: newTeacherName.trim(), 
                                                    email: newTeacherEmail.trim(), 
                                                    defaultRoomId: newTeacherRoom,
                                                    order: teachers.length 
                                                })
                                            })
                                            .then(res => {
                                                if(res.ok) {
                                                    setNewTeacherName('');
                                                    setNewTeacherEmail('');
                                                    setNewTeacherRoom('');
                                                    fetchData();
                                                } else {
                                                    alert('Failed to add teacher');
                                                }
                                            });
                                        }
                                    }}
                                >
                                    Add
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* TEACHERS LIST */}
                    <TableContainer component={Box} sx={{ overflowX: 'auto' }}>
                      <Table sx={{ minWidth: 1000, tableLayout: 'fixed' }}>
                        <TableHead>
                          <TableRow sx={{ '& th': { fontWeight: 800, color: 'text.secondary', borderBottom: '2px solid #f0f0f0', py: 2 } }}>
                            <TableCell sx={{ width: '250px' }}>Teacher Name</TableCell>
                            <TableCell sx={{ width: '400px' }}>Email</TableCell>
                            <TableCell sx={{ width: '200px' }} align="right">Default Room</TableCell>
                            <TableCell align="right" width={100}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[...teachers].sort((a, b) => a.name.localeCompare(b.name)).map((teacher) => (
                            <TableRow 
                              key={teacher._id}
                              sx={{ 
                                '&:hover': { bgcolor: '#fcfcfc' },
                                '& td': { py: 2.5, borderBottom: '1px solid #f5f5f5' }
                              }}
                            >
                              <TableCell>
                                <Typography fontWeight={600} fontSize="1.1rem">{teacher.name}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography color={teacher.email ? 'text.primary' : 'text.disabled'}>
                                    {teacher.email || 'No email'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={500} color="text.secondary">
                                    {rooms.find(r => r._id === teacher.defaultRoomId)?.name || 'None'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => setTeacherToEdit(teacher)} 
                                        color="primary"
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    
                                    {schedules.some(s => s.teacherId === teacher._id) && (
                                        <IconButton size="small" onClick={() => handleOpenUsageModal(null, teacher._id)} title="Find Usages">
                                            <SearchIcon fontSize="small" color="primary" />
                                        </IconButton>
                                    )}
                                    <IconButton 
                                        size="small" 
                                        color="error"
                                        onClick={() => {
                                            if(window.confirm('Delete this teacher?')) {
                                                fetchWithAuth(`${API_URL}/teachers/${teacher._id}`, { method: 'DELETE' })
                                                .then(res => {
                                                    if(res.ok) fetchData();
                                                    else alert('Failed to delete');
                                                });
                                            }
                                        }}
                                        disabled={schedules.some(s => s.teacherId === teacher._id)}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {/* EDIT DIALOG */}
                    {teacherToEdit && (
                        <Dialog open={true} onClose={() => setTeacherToEdit(null)} maxWidth="sm" fullWidth>
                            <DialogTitle sx={{ fontWeight: 800 }}>Edit Teacher</DialogTitle>
                            <DialogContent>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                                    <TextField
                                        label="Name"
                                        value={teacherToEdit.name}
                                        onChange={(e) => setTeacherToEdit({ ...teacherToEdit, name: e.target.value })}
                                        fullWidth
                                        variant="outlined"
                                    />
                                    <TextField
                                        label="Email"
                                        value={teacherToEdit.email}
                                        onChange={(e) => setTeacherToEdit({ ...teacherToEdit, email: e.target.value })}
                                        fullWidth
                                        variant="outlined"
                                    />
                                    <FormControl fullWidth>
                                        <InputLabel>Default Room</InputLabel>
                                        <Select
                                            value={teacherToEdit.defaultRoomId || ''}
                                            label="Default Room"
                                            onChange={(e) => setTeacherToEdit({ ...teacherToEdit, defaultRoomId: e.target.value })}
                                        >
                                            <MenuItem value=""><em>None</em></MenuItem>
                                            {rooms.map(r => <MenuItem key={r._id} value={r._id}>{r.name}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </DialogContent>
                            <DialogActions sx={{ p: 3 }}>
                                <Button onClick={() => setTeacherToEdit(null)}>Cancel</Button>
                                <Button 
                                    variant="contained" 
                                    onClick={() => {
                                        fetchWithAuth(`${API_URL}/teachers/${teacherToEdit._id}`, {
                                            method: 'PUT',
                                            body: JSON.stringify({ 
                                                name: teacherToEdit.name, 
                                                email: teacherToEdit.email, 
                                                defaultRoomId: teacherToEdit.defaultRoomId,
                                                order: teacherToEdit.order
                                            })
                                        })
                                        .then(res => {
                                            if(res.ok) {
                                                fetchData();
                                                setTeacherToEdit(null);
                                            } else {
                                                alert('Failed to update teacher');
                                            }
                                        });
                                    }}
                                >
                                    Save Changes
                                </Button>
                            </DialogActions>
                        </Dialog>
                    )}

                    {teachers.length === 0 && (
                        <Box sx={{ py: 10, textAlign: 'center' }}>
                            <Typography variant="h6" color="text.disabled">No teachers added yet.</Typography>
                        </Box>
                    )}
                  </Container>
                </Box>
              </Dialog>

              <Modal open={isTeacherUsageModalOpen} onClose={handleCloseTeacherUsageModal}>
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, p: 4 }}>
                    <Typography variant="h6" component="h2" sx={{mb: 2}}>
                        Usage of "{teacherUsageData.teacherName}"
                    </Typography>
                    <Typography variant="body1" sx={{mb: 2}}>
                        This teacher is used on the following dates. Click delete to remove all entries for a specific day.
                    </Typography>
                    <Box sx={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ccc', p: 1, mb: 2 }}>
                        {teacherUsageData.dates.length > 0 ? (
                            teacherUsageData.dates.map((dateInfo, index) => (
                                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 0.5 }}>
                                    <Typography>{dateInfo.formatted}</Typography>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => handleDeleteTeacherUsagesForDay(teachers.find(t => t.name === teacherUsageData.teacherName)?._id, dateInfo.raw)}
                                        title={`Delete all '${teacherUsageData.teacherName}' entries for ${dateInfo.formatted}`}
                                    >
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </Box>
                            ))
                        ) : (
                            <Typography>No usages found. You can now delete this teacher.</Typography>
                        )}
                    </Box>
                    <Box sx={{mt: 3, display: 'flex', justifyContent: 'flex-end'}}>
                        <Button onClick={handleCloseTeacherUsageModal}>Close</Button>
                    </Box>
                </Box>
              </Modal>

              <Dialog 
                open={isRoomModalOpen} 
                onClose={handleCloseRoomModal}
                fullScreen
              >
                <AppBar sx={{ position: 'sticky', top: 0, bgcolor: 'white', color: 'black', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', zIndex: 1100 }}>
                  <Container maxWidth="lg">
                    <Toolbar disableGutters>
                      <IconButton edge="start" color="inherit" onClick={handleCloseRoomModal} aria-label="close">
                        <ArrowBackIosIcon />
                      </IconButton>
                      <Typography sx={{ ml: 2, flex: 1, fontWeight: 800, letterSpacing: '-0.5px' }} variant="h5">
                        Room Management
                      </Typography>
                    </Toolbar>
                  </Container>
                </AppBar>

                <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', pb: 10 }}>
                  <Container maxWidth="lg" sx={{ mt: 4 }}>
                    
                    {/* ADD NEW ROOM - STICKY HEADER */}
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 3, 
                            mb: 2, 
                            borderRadius: 4, 
                            border: '1px solid #e0e0e0', 
                            bgcolor: '#f8f9fa',
                            position: 'sticky',
                            top: 80,
                            zIndex: 1000
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '1px' }}>
                            Quick Add New Room
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={10}>
                                <TextField
                                    placeholder="Room Name"
                                    value={newRoomName}
                                    onChange={(e) => setNewRoomName(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    disableElevation
                                    sx={{ borderRadius: 2, py: 1, fontWeight: 700 }}
                                    onClick={() => {
                                        if (newRoomName.trim()) {
                                            fetchWithAuth(`${API_URL}/rooms`, {
                                                method: 'POST',
                                                body: JSON.stringify({ 
                                                    name: newRoomName.trim(), 
                                                    order: rooms.length 
                                                })
                                            })
                                            .then(res => {
                                                if(res.ok) {
                                                    setNewRoomName('');
                                                    fetchData();
                                                } else {
                                                    alert('Failed to add room');
                                                }
                                            });
                                        }
                                    }}
                                >
                                    Add
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* ROOMS LIST */}
                    <TableContainer component={Box} sx={{ overflowX: 'auto' }}>
                      <Table sx={{ minWidth: 600, tableLayout: 'fixed' }}>
                        <TableHead>
                          <TableRow sx={{ '& th': { fontWeight: 800, color: 'text.secondary', borderBottom: '2px solid #f0f0f0', py: 2 } }}>
                            <TableCell>Room Name</TableCell>
                            <TableCell align="right" width={120}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[...rooms].sort((a, b) => a.name.localeCompare(b.name)).map((room) => (
                            <TableRow 
                              key={room._id}
                              sx={{ 
                                '&:hover': { bgcolor: '#fcfcfc' },
                                '& td': { py: 2.5, borderBottom: '1px solid #f5f5f5' }
                              }}
                            >
                              <TableCell>
                                <Typography fontWeight={600} fontSize="1.1rem">{room.name}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                    <IconButton 
                                        size="small" 
                                        color="primary"
                                        onClick={() => setRoomToEdit(room)}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    {schedules.some(s => s.roomId === room._id) && (
                                        <IconButton size="small" onClick={() => handleOpenRoomUsageModal(room._id)} title="Find Usages">
                                            <SearchIcon fontSize="small" color="primary" />
                                        </IconButton>
                                    )}
                                    <IconButton 
                                        size="small" 
                                        color="error"
                                        onClick={() => {
                                            if(window.confirm(`Delete room '${room.name}'?`)) {
                                                fetchWithAuth(`${API_URL}/rooms/${room._id}`, { method: 'DELETE' })
                                                .then(res => {
                                                    if(res.ok) fetchData();
                                                    else res.json().then(data => alert(data.message || 'Failed to delete'));
                                                });
                                            }
                                        }}
                                        disabled={schedules.some(s => s.roomId === room._id)}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {/* EDIT ROOM DIALOG */}
                    {roomToEdit && (
                        <Dialog open={true} onClose={() => setRoomToEdit(null)} maxWidth="sm" fullWidth>
                            <DialogTitle sx={{ fontWeight: 800 }}>Edit Room</DialogTitle>
                            <DialogContent>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                                    <TextField
                                        label="Room Name"
                                        value={roomToEdit.name}
                                        onChange={(e) => setRoomToEdit({ ...roomToEdit, name: e.target.value })}
                                        fullWidth
                                        variant="outlined"
                                    />
                                </Box>
                            </DialogContent>
                            <DialogActions sx={{ p: 3 }}>
                                <Button onClick={() => setRoomToEdit(null)}>Cancel</Button>
                                <Button 
                                    variant="contained" 
                                    onClick={() => {
                                        fetchWithAuth(`${API_URL}/rooms/${roomToEdit._id}`, {
                                            method: 'PUT',
                                            body: JSON.stringify({ 
                                                name: roomToEdit.name, 
                                                order: roomToEdit.order
                                            })
                                        })
                                        .then(res => {
                                            if(res.ok) {
                                                fetchData();
                                                setRoomToEdit(null);
                                            } else {
                                                alert('Failed to update room');
                                            }
                                        });
                                    }}
                                >
                                    Save Changes
                                </Button>
                            </DialogActions>
                        </Dialog>
                    )}

                    {rooms.length === 0 && (
                        <Box sx={{ py: 10, textAlign: 'center' }}>
                            <Typography variant="h6" color="text.disabled">No rooms added yet.</Typography>
                        </Box>
                    )}
                  </Container>
                </Box>
              </Dialog>
              
              <Modal open={isRoomUsageModalOpen} onClose={handleCloseRoomUsageModal}>
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, bgcolor: 'background.paper', border: '2px solid #000', boxShadow: 24, p: 4 }}>
                    <Typography variant="h6" component="h2" sx={{mb: 2}}>
                        Usage of "{roomUsageData.roomName}"
                    </Typography>
                    <Typography variant="body1" sx={{mb: 2}}>
                        This room is used on the following dates. Click delete to remove all entries for a specific day.
                    </Typography>
                    <Box sx={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ccc', p: 1, mb: 2 }}>
                        {roomUsageData.dates.length > 0 ? (
                            roomUsageData.dates.map((dateInfo, index) => (
                                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 0.5 }}>
                                    <Typography>{dateInfo.formatted}</Typography>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => handleDeleteRoomUsagesForDay(rooms.find(r => r.name === roomUsageData.roomName)?._id, dateInfo.raw)}
                                        title={`Delete all '${roomUsageData.roomName}' entries for ${dateInfo.formatted}`}
                                    >
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </Box>
                            ))
                        ) : (
                            <Typography>No usages found. You can now delete this room.</Typography>
                        )}
                    </Box>
                    <Box sx={{mt: 3, display: 'flex', justifyContent: 'flex-end'}}>
                        <Button onClick={handleCloseRoomUsageModal}>Close</Button>
                    </Box>
                </Box>
              </Modal>

              <Dialog 
                open={isLessonCounterModalOpen} 
                onClose={() => setIsLessonCounterModalOpen(false)}
                fullScreen
              >
                <AppBar sx={{ position: 'sticky', top: 0, bgcolor: 'white', color: 'black', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', zIndex: 1100 }}>
                  <Container maxWidth="lg">
                    <Toolbar disableGutters>
                      <IconButton edge="start" color="inherit" onClick={() => setIsLessonCounterModalOpen(false)} aria-label="close">
                        <ArrowBackIosIcon />
                      </IconButton>
                      <Typography sx={{ ml: 2, flex: 1, fontWeight: 800, letterSpacing: '-0.5px' }} variant="h5">
                        Lesson Counter
                      </Typography>
                    </Toolbar>
                  </Container>
                </AppBar>

                <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', pb: 10 }}>
                  <Container maxWidth="lg" sx={{ mt: 4 }}>
                    
                    {/* FILTERS - STICKY HEADER STYLE */}
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 3, 
                            mb: 6, 
                            borderRadius: 4, 
                            border: '1px solid #e0e0e0', 
                            bgcolor: '#f8f9fa',
                            position: 'sticky',
                            top: 80,
                            zIndex: 1000
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '1px' }}>
                            Filter Lessons
                        </Typography>
                        <Grid container spacing={2} alignItems="center" sx={{ justifyContent: 'flex-start' }}>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    label="Start Date"
                                    type="date"
                                    value={counterStartDate}
                                    onChange={(e) => setCounterStartDate(e.target.value)}
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    label="End Date"
                                    type="date"
                                    value={counterEndDate}
                                    onChange={(e) => setCounterEndDate(e.target.value)}
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <FormControl size="small" sx={{ width: '200px' }}>
                                    <InputLabel>Select Teacher...</InputLabel>
                                    <Select
                                        value={counterSelectedTeacher}
                                        label="Select Teacher..."
                                        onChange={(e) => setCounterSelectedTeacher(e.target.value)}
                                        sx={{ bgcolor: 'white', borderRadius: 2 }}
                                    >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {teachers.map(t => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* RESULT SECTION */}
                    <Grid container spacing={6} sx={{ mt: 2 }}>
                        {/* LEFT: TOTAL CIRCLE */}
                        <Grid item xs={12} md={5}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}>
                                    Total Hours
                                </Typography>
                                {counterStartDate && counterEndDate && (
                                    <Typography variant="caption" sx={{ mb: 2, fontWeight: 700, color: 'primary.main' }}>
                                        {formatDate(counterStartDate)} — {formatDate(counterEndDate)}
                                    </Typography>
                                )}
                                <Paper 
                                    elevation={0} 
                                    sx={{ 
                                        p: 6, 
                                        borderRadius: '50%', 
                                        width: 280, 
                                        height: 280, 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        border: '8px solid #1976d2',
                                        bgcolor: '#fff',
                                        boxShadow: '0 20px 40px rgba(25, 118, 210, 0.1)'
                                    }}
                                >
                                    <Typography variant="h1" sx={{ fontWeight: 900, color: '#1976d2', lineHeight: 1 }}>
                                        {lessonCount}
                                    </Typography>
                                    <Typography variant="button" sx={{ mt: 1, fontWeight: 800, color: 'text.disabled' }}>
                                        Hours
                                    </Typography>
                                </Paper>
                                
                                {simultaneousLessons.length > 0 && (
                                    <Box sx={{ mt: 4, textAlign: 'center' }}>
                                        <Chip 
                                            label={`${simultaneousLessons.length} Simultaneous Time Slots`} 
                                            color="warning" 
                                            variant="outlined" 
                                            sx={{ fontWeight: 700 }} 
                                        />
                                    </Box>
                                )}
                                
                                {!counterSelectedTeacher && (
                                    <Typography sx={{ mt: 4, color: 'error.main', fontWeight: 700 }}>
                                        * Please select a teacher to see the count
                                    </Typography>
                                )}
                            </Box>
                        </Grid>

                        {/* RIGHT: BREAKDOWN */}
                        <Grid item xs={12} md={7}>
                            <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>Breakdown by Grade & Subject</Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: '300px', overflowY: 'auto', mb: 4 }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow sx={{ '& th': { fontWeight: 800, bgcolor: '#f5f5f5' } }}>
                                            <TableCell>Grade</TableCell>
                                            <TableCell>Subject</TableCell>
                                            <TableCell align="right">Hours</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {lessonBreakdown.length > 0 ? (
                                            lessonBreakdown.map((row, idx) => (
                                                <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                                                    <TableCell sx={{ fontWeight: 700 }}>{row.grade === 'N/A' ? 'N/A' : `Grade ${row.grade}`}</TableCell>
                                                    <TableCell>{subjects.find(s => s._id === row.subjectId)?.name || 'Custom / Other'}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 800, color: 'primary.main' }}>{row.count}</TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                                                    {counterSelectedTeacher ? 'No lessons found for this period.' : 'Select a teacher to see breakdown.'}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {simultaneousLessons.length > 0 && (
                                <>
                                    <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: 'warning.main' }}>Simultaneous Lessons Breakdown</Typography>
                                    <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                                        Teacher was mentioned in multiple classes during these time slots.
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: '300px', overflowY: 'auto' }}>
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow sx={{ '& th': { fontWeight: 800, bgcolor: '#fff9c4' } }}>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell>Time Slot</TableCell>
                                                    <TableCell>Classes & Subjects</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {simultaneousLessons.map((group, idx) => (
                                                    <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#fffde7' } }}>
                                                        <TableCell sx={{ fontWeight: 700 }}>{formatDate(group.day)}</TableCell>
                                                        <TableCell>{timeSlots.find(ts => ts.id == group.timeSlotId)?.time || group.timeSlotId}</TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                {group.events.map((e, eIdx) => (
                                                                    <Chip 
                                                                        key={eIdx}
                                                                        label={`G${e.grade}: ${subjects.find(s => s._id === e.subjectId)?.name || 'Custom'}`}
                                                                        size="small"
                                                                        sx={{ fontSize: '10px', height: '20px' }}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}
                        </Grid>
                    </Grid>

                  </Container>
                </Box>
              </Dialog>
              
              <Dialog 
                open={isSendMailModalOpen} 
                onClose={handleCloseMailModal}
                fullScreen
              >
                <AppBar sx={{ position: 'sticky', top: 0, bgcolor: 'white', color: 'black', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', zIndex: 1100 }}>
                  <Container maxWidth="lg">
                    <Toolbar disableGutters>
                      <IconButton edge="start" color="inherit" onClick={handleCloseMailModal} aria-label="close">
                        <ArrowBackIosIcon />
                      </IconButton>
                      <Typography sx={{ ml: 2, flex: 1, fontWeight: 800, letterSpacing: '-0.5px' }} variant="h5">
                        Send Schedule via Email
                      </Typography>
                      <Button 
                        color="primary" 
                        variant="contained" 
                        onClick={handleSendMail} 
                        disabled={sendingMail}
                        startIcon={sendingMail ? <CircularProgress size={20} color="inherit" /> : null}
                        sx={{ borderRadius: 8, px: 4, fontWeight: 700, boxShadow: 'none' }}
                      >
                        {sendingMail ? 'Sending...' : 'Send Emails'}
                      </Button>
                    </Toolbar>
                  </Container>
                </AppBar>

                <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', pb: 10 }}>
                  <Container maxWidth="lg" sx={{ mt: 4 }}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={5}>
                             <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Email Content</Typography>
                             <TextField
                                label="Message Body"
                                multiline
                                rows={12}
                                value={emailBody}
                                onChange={(e) => setEmailBody(e.target.value)}
                                fullWidth
                                variant="outlined"
                                placeholder="Write your message here..."
                                sx={{ bgcolor: '#f9f9f9', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                                The selected schedules will be automatically attached as PDF files.
                            </Alert>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" fontWeight={700}>Recipients</Typography>
                                <Button 
                                    size="small" 
                                    onClick={() => {
                                        const allIds = emailRecipients.map(r => r.id);
                                        const allSelected = allIds.every(id => selectedRecipients[id]);
                                        const newSelection = {};
                                        if (!allSelected) {
                                            allIds.forEach(id => newSelection[id] = true);
                                        }
                                        setSelectedRecipients(newSelection);
                                    }}
                                >
                                    {emailRecipients.every(r => selectedRecipients[r.id]) ? "Deselect All" : "Select All"}
                                </Button>
                            </Box>
                            
                            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                <Tabs 
                                    value={mailTab} 
                                    onChange={(e, v) => setMailTab(v)} 
                                    variant="fullWidth" 
                                    sx={{ borderBottom: '1px solid #eee', bgcolor: '#f5f5f5' }}
                                >
                                    <Tab label="General" sx={{ fontWeight: 700 }} />
                                    <Tab label="Teachers" sx={{ fontWeight: 700 }} />
                                    <Tab label="Students" sx={{ fontWeight: 700 }} />
                                </Tabs>
                                <Box sx={{ height: '400px', overflowY: 'auto', p: 2 }}>
                                    {emailRecipients
                                        .filter(r => {
                                            if (mailTab === 0) return r.type === 'general';
                                            if (mailTab === 1) return r.type === 'teacher';
                                            if (mailTab === 2) return r.type === 'student';
                                            return true;
                                        })
                                        .map(r => (
                                            <Paper 
                                                key={r.id} 
                                                elevation={0}
                                                sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    p: 1.5, 
                                                    mb: 1, 
                                                    borderRadius: 2, 
                                                    bgcolor: selectedRecipients[r.id] ? '#e3f2fd' : 'transparent',
                                                    border: '1px solid',
                                                    borderColor: selectedRecipients[r.id] ? '#90caf9' : 'transparent',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s'
                                                }}
                                                onClick={() => setSelectedRecipients(prev => ({ ...prev, [r.id]: !prev[r.id] }))}
                                            >
                                                <Checkbox
                                                    checked={!!selectedRecipients[r.id]}
                                                    color="primary"
                                                    disableRipple
                                                    sx={{ p: 0.5, mr: 1.5 }}
                                                />
                                                <Box>
                                                    <Typography fontWeight={600} variant="body2">{r.name}</Typography>
                                                    {r.email && <Typography variant="caption" color="text.secondary">{r.email}</Typography>}
                                                </Box>
                                            </Paper>
                                    ))}
                                    {emailRecipients.filter(r => {
                                            if (mailTab === 0) return r.type === 'general';
                                            if (mailTab === 1) return r.type === 'teacher';
                                            if (mailTab === 2) return r.type === 'student';
                                            return false;
                                    }).length === 0 && (
                                        <Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled' }}>
                                            <Typography>No recipients in this category.</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                  </Container>
                </Box>
              </Dialog>
              <Dialog
                open={isConfirmOpen}
                onClose={handleConfirmClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
              >
                <DialogTitle id="alert-dialog-title">
                  {"Conflict Detected"}
                </DialogTitle>
                <DialogContent>
                  <DialogContentText id="alert-dialog-description">
                    {confirmMessage}
                    <br/><br/>
                    Do you want to save this event anyway?
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleConfirmClose}>Cancel</Button>
                  <Button onClick={handleConfirmAction} autoFocus>
                    Save Anyway
                  </Button>
                                </DialogActions>
                              </Dialog>
              
                              <Backdrop
                                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                                open={isSaving}
                              >
                                <CircularProgress color="inherit" />
                              </Backdrop>
              
              <Dialog 
                open={isRolesModalOpen} 
                onClose={() => setIsRolesModalOpen(false)}
                fullScreen
              >
                <AppBar sx={{ position: 'sticky', top: 0, bgcolor: 'white', color: 'black', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', zIndex: 1100 }}>
                  <Container maxWidth="lg">
                    <Toolbar disableGutters>
                      <IconButton edge="start" color="inherit" onClick={() => setIsRolesModalOpen(false)} aria-label="close">
                        <ArrowBackIosIcon />
                      </IconButton>
                      <Typography sx={{ ml: 2, flex: 1, fontWeight: 800, letterSpacing: '-0.5px' }} variant="h5">
                        User Roles
                      </Typography>
                    </Toolbar>
                  </Container>
                </AppBar>

                <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', pb: 10 }}>
                  <Container maxWidth="lg" sx={{ mt: 4 }}>
                    
                    {/* ADD NEW USER - STICKY HEADER */}
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 3, 
                            mb: 2, 
                            borderRadius: 4, 
                            border: '1px solid #e0e0e0', 
                            bgcolor: '#f8f9fa',
                            position: 'sticky',
                            top: 80,
                            zIndex: 1000
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '1px' }}>
                            Quick Add New User
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={4}>
                                <TextField 
                                    placeholder="Email" 
                                    value={newUserEmail} 
                                    onChange={(e) => setNewUserEmail(e.target.value)} 
                                    fullWidth 
                                    size="small" 
                                    sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField 
                                    placeholder="Name (Optional)" 
                                    value={newUserName} 
                                    onChange={(e) => setNewUserName(e.target.value)} 
                                    fullWidth 
                                    size="small" 
                                    sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <FormControl fullWidth size="small">
                                    <Select 
                                        value={newUserRole} 
                                        onChange={(e) => setNewUserRole(e.target.value)}
                                        sx={{ bgcolor: 'white', borderRadius: 2 }}
                                    >
                                        <MenuItem value="admin">Admin</MenuItem>
                                        <MenuItem value="editor">Editor</MenuItem>
                                        <MenuItem value="counter">Counter</MenuItem>
                                        <MenuItem value="viewer">Viewer</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <Button 
                                    variant="contained" 
                                    fullWidth 
                                    onClick={handleAddUser}
                                    disableElevation
                                    sx={{ borderRadius: 2, py: 1, fontWeight: 700 }}
                                >
                                    Add User
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* USERS LIST */}
                    <TableContainer component={Box} sx={{ overflowX: 'auto' }}>
                      <Table sx={{ minWidth: 800, tableLayout: 'fixed' }}>
                        <TableHead>
                          <TableRow sx={{ '& th': { fontWeight: 800, color: 'text.secondary', borderBottom: '2px solid #f0f0f0', py: 2 } }}>
                            <TableCell>User Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell width={150}>Role</TableCell>
                            <TableCell align="right" width={100}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {usersList.map(u => (
                            <TableRow 
                              key={u._id}
                              sx={{ 
                                '&:hover': { bgcolor: '#fcfcfc' },
                                '& td': { py: 2.5, borderBottom: '1px solid #f5f5f5' }
                              }}
                            >
                              <TableCell>
                                <Typography fontWeight={600}>{u.name || <em>Unknown Name</em>}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography color="text.secondary">{u.email}</Typography>
                              </TableCell>
                              <TableCell>
                                <FormControl size="small" fullWidth variant="standard">
                                    <Select
                                        value={u.role || 'viewer'}
                                        onChange={(e) => handleUpdateRole(u._id, e.target.value)}
                                        disabled={u.email === 'a.bukhariev@krumbach.school'}
                                        disableUnderline
                                        sx={{ fontWeight: 500 }}
                                    >
                                        <MenuItem value="admin">Admin</MenuItem>
                                        <MenuItem value="editor">Editor</MenuItem>
                                        <MenuItem value="counter">Counter</MenuItem>
                                        <MenuItem value="viewer">Viewer</MenuItem>
                                    </Select>
                                </FormControl>
                              </TableCell>
                              <TableCell align="right">
                                <IconButton 
                                    color="error" 
                                    onClick={() => handleDeleteUser(u._id)}
                                    disabled={u.email === 'a.bukhariev@krumbach.school'}
                                    size="small"
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                  </Container>
                </Box>
              </Dialog>
              
                              <Dialog 
                                open={isSettingsModalOpen} 
                                onClose={() => setIsSettingsModalOpen(false)}
                                fullScreen
                              >
                                <AppBar sx={{ position: 'sticky', top: 0, bgcolor: 'white', color: 'black', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', zIndex: 1100 }}>
                                  <Container maxWidth="lg">
                                    <Toolbar disableGutters>
                                      <IconButton edge="start" color="inherit" onClick={() => setIsSettingsModalOpen(false)} aria-label="close">
                                        <ArrowBackIosIcon />
                                      </IconButton>
                                      <Typography sx={{ ml: 2, flex: 1, fontWeight: 800, letterSpacing: '-0.5px' }} variant="h5">
                                        System Settings
                                      </Typography>
                                      <Button 
                                        color="primary" 
                                        variant="contained" 
                                        onClick={() => {
                                            fetchWithAuth(`${API_URL}/config`, {
                                                method: 'PUT',
                                                body: JSON.stringify({ allTeachersEmail, gradeEmails })
                                            })
                                            .then(res => {
                                                if(res.ok) setIsSettingsModalOpen(false);
                                                else alert('Failed to save settings');
                                            });
                                        }} 
                                        sx={{ borderRadius: 8, px: 4, fontWeight: 700, boxShadow: 'none' }}
                                      >
                                        Save Settings
                                      </Button>
                                    </Toolbar>
                                  </Container>
                                </AppBar>
                                
                                <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', pb: 10 }}>
                                  <Container maxWidth="md" sx={{ mt: 6 }}>
                                    <Paper elevation={0} sx={{ p: 4, mb: 4, borderRadius: 4, border: '1px solid #e0e0e0' }}>
                                        <Typography variant="h6" fontWeight={800} sx={{ mb: 3, color: '#1976d2' }}>General Configuration</Typography>
                                        <TextField
                                            label="All Teachers Email Address"
                                            value={allTeachersEmail}
                                            onChange={(e) => setAllTeachersEmail(e.target.value)}
                                            fullWidth
                                            variant="outlined"
                                            helperText="This email will be used for the 'All Teachers' recipient option."
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Paper>

                                    <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid #e0e0e0' }}>
                                        <Typography variant="h6" fontWeight={800} sx={{ mb: 3, color: '#1976d2' }}>Class Email Addresses</Typography>
                                        <Grid container spacing={3}>
                                            {grades.map(g => (
                                                <Grid item xs={12} sm={6} key={g}>
                                                    <TextField
                                                        label={`Grade ${g} Email`}
                                                        value={gradeEmails[g] || ''}
                                                        onChange={(e) => setGradeEmails(prev => ({ ...prev, [g]: e.target.value }))}
                                                        fullWidth
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Paper>
                                  </Container>
                                </Box>
                              </Dialog>

                              <Dialog 
                                open={isLogsModalOpen} 
                                onClose={() => setIsLogsModalOpen(false)}
                                fullScreen
                              >
                                <AppBar sx={{ position: 'sticky', top: 0, bgcolor: 'white', color: 'black', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', zIndex: 1100 }}>
                                  <Container maxWidth="lg">
                                    <Toolbar disableGutters>
                                      <IconButton edge="start" color="inherit" onClick={() => setIsLogsModalOpen(false)} aria-label="close">
                                        <ArrowBackIosIcon />
                                      </IconButton>
                                      <Typography sx={{ ml: 2, flex: 1, fontWeight: 800, letterSpacing: '-0.5px' }} variant="h5">
                                        Audit Logs
                                      </Typography>
                                    </Toolbar>
                                  </Container>
                                </AppBar>
                                <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', pb: 10 }}>
                                  <Container maxWidth="lg" sx={{ mt: 4 }}>
                                    <LogsView user={user} />
                                  </Container>
                                </Box>
                              </Dialog>

      <div id="pdf-container" style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '210mm', height: '297mm', overflow: 'hidden' }}></div>
      </Container>
    </ThemeProvider>
  );
}

export default App;